import React, { useEffect, useMemo, useRef, useState } from "react";
import { supabase } from "../../../utils/supabase";
import { loadBillingAttachments } from "../../../utils/billing-attachments";

// --- Config destinatarios de facturación ---
const BILLING_TO = [
  "administracion@calins.com.ar,gestion@calins.com.ar,administracion2@calins.com.ar,finanzas@calins.com.ar ",
];
const BILLING_CC = ["finanzas@calins.com.ar"];

// --- helpers ---
const fmt = (n) =>
  new Intl.NumberFormat("es-AR", {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  }).format(Number(n || 0));

function num(v, d = 0) {
  const normalized = typeof v === "string" ? v.trim().replace(",", ".") : v;
  const n = Number(normalized);
  return Number.isFinite(n) ? n : d;
}

function isDecimalDraft(value) {
  return /^\d*(?:[.,]\d*)?$/.test(String(value ?? ""));
}

// Acepta OS-5594-001 y OS-5594001 → 5594
function deriveCentroCtoFromCode(code) {
  const s = String(code || "");
  const m = s.match(/OS-(\d{4})-?\d+/);
  return m ? m[1] : "";
}

function normalizeServNro(code) {
  const m = String(code || "").match(/OS-(\d+-\d+)/);
  return m ? m[1] : String(code || "");
}

/**
 * TAG — CATEGORÍA: X — MARCA: Y, MOD: Z, S/N: W — PO: <baker_code>
 */
function buildDescription(i) {
  // Construir la parte base con tag y categoría
  let parts = [];

  if (i.instrument_tag) {
    parts.push(`TAG: ${i.instrument_tag}`);
  }

  if (i.instrument_category) {
    parts.push(`CATEGORÍA: ${i.instrument_category}`);
  }

  const base = parts.join(" — ");

  // Agregar marca, modelo y serial
  const serial = String(i.instrument_serial ?? "").trim() || "sin informar";
  const details = `MARCA: ${i.instrument_brand ?? ""}, MOD: ${
    i.instrument_model ?? ""
  }, S/N: ${serial}`;

  const fullBase = base ? `${base} — ${details}` : details;

  if (i.baker_code) return `${fullBase} — PO: ${i.baker_code}`;
  return fullBase;
}

function safeFileName(name) {
  return String(name || "archivo")
    .trim()
    .replace(/\s+/g, "_")
    .replace(/[^\w.\-()]/g, "");
}

function fileNameFromStorageUrl(url) {
  if (!url) return "";
  try {
    const pathname = new URL(String(url), window.location.origin).pathname;
    const storedName = decodeURIComponent(pathname.split("/").filter(Boolean).pop() || "");
    // Los escaneos se guardan como "<timestamp>-<nombre original seguro>".
    return storedName.replace(/^\d{10,16}-/, "");
  } catch {
    const storedName = decodeURIComponent(
      String(url).split("?")[0].split("/").filter(Boolean).pop() || ""
    );
    return storedName.replace(/^\d{10,16}-/, "");
  }
}

function todayInputYMD() {
  const now = new Date();
  const offset = now.getTimezoneOffset();
  return new Date(now.getTime() - offset * 60000).toISOString().slice(0, 10);
}

function isPdfUrl(url) {
  const s = String(url || "").toLowerCase();
  return s.includes(".pdf") || s.includes("application/pdf");
}

function genLocalId(prefix = "row") {
  if (typeof crypto !== "undefined" && crypto?.randomUUID) {
    return `${prefix}_${crypto.randomUUID()}`;
  }
  return `${prefix}_${Date.now()}_${Math.random().toString(36).slice(2, 10)}`;
}

function guessFileKind(row) {
  const raw = String(
    row?.file?.type ||
      row?.file_type ||
      row?.file_name ||
      row?.pdf_url ||
      ""
  ).toLowerCase();
  if (raw.includes("pdf") || raw.endsWith(".pdf")) return "pdf";
  if (
    raw.includes("png") ||
    raw.includes("jpg") ||
    raw.includes("jpeg") ||
    raw.includes("webp") ||
    raw.endsWith(".png") ||
    raw.endsWith(".jpg") ||
    raw.endsWith(".jpeg") ||
    raw.endsWith(".webp")
  ) {
    return "image";
  }
  return raw ? "file" : "";
}

function extractBaseNumber(raw) {
  const s = String(raw || "").trim();
  if (!s) return null;
  const m = s.match(/\d{3,}/);
  return m ? Number(m[0]) : null;
}

function normalizeBillingStatus(status) {
  switch (String(status || "").toLowerCase()) {
    case "requested":
      return "sent";
    case "issued":
      return "invoiced";
    case "closed":
      return "cancelled";
    case "draft":
    case "sent":
    case "invoiced":
    case "cancelled":
      return String(status).toLowerCase();
    default:
      return "draft";
  }
}

function billingStatusLabel(status) {
  switch (normalizeBillingStatus(status)) {
    case "sent":
      return "Enviado para facturar";
    case "invoiced":
      return "Facturado";
    case "cancelled":
      return "Cerrada";
    case "draft":
    default:
      return "Borrador";
  }
}

function invoiceRowKey(row) {
  return String(row?.service_price_id || row?.instrument_id || "");
}

function invoiceRowSnapshot(row) {
  return {
    qty: num(row?.qty ?? 1, 1),
    price: num(row?.price ?? 0, 0),
    note: String(row?.note ?? ""),
    currency: String(row?.currency || "USD BNA"),
  };
}

function formatInvoiceChangeSummary(rows, originalMap) {
  const changed = [];
  (rows || []).forEach((row) => {
    const key = invoiceRowKey(row);
    const before = originalMap.get(key);
    if (!before) return;

    const next = invoiceRowSnapshot(row);
    const diffs = [];
    if (next.qty !== before.qty) diffs.push(`cant ${before.qty} -> ${next.qty}`);
    if (next.price !== before.price) diffs.push(`precio ${fmt(before.price)} -> ${fmt(next.price)}`);
    if (diffs.length) {
      changed.push(`${row.descripcion || row.serv_nro || key}: ${diffs.join(", ")}`);
    }
  });
  return changed;
}

function pricingAutoSaveSignature(rows, generalObservations) {
  return JSON.stringify({
    rows: (rows || []).map((row) => ({
      key: invoiceRowKey(row),
      ...invoiceRowSnapshot(row),
    })),
    generalObservations: String(generalObservations || ""),
  });
}


// ---------------- EXTRAS (service_price_extras) ----------------
function ExtrasModal({
  open,
  osId,
  osCode,
  onClose,
  onChanged,
  setToast = () => {},
}) {
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [rows, setRows] = useState([]);

  const [editingId, setEditingId] = useState(null);
  const [form, setForm] = useState({
    description: "",
    qty: 1,
    price: 0,
    currency: "USD BNA",
    note: "",
  });

  const totalExtras = useMemo(() => {
    return (rows || []).reduce((acc, r) => acc + num(r.qty, 0) * num(r.price, 0), 0);
  }, [rows]);

  const resetForm = () => {
    setEditingId(null);
    setForm({ description: "", qty: 1, price: 0, currency: "USD BNA", note: "" });
  };

  const load = async () => {
    if (!open || !osId) return;
    try {
      setLoading(true);
      const { data, error } = await supabase
        .from("service_price_extras")
        .select("id,os_id,description,qty,price,currency,note,created_at")
        .eq("os_id", osId)
        .order("created_at", { ascending: true });

      if (error) throw error;
      setRows(data || []);
    } catch (e) {
      console.error("[ExtrasModal] load", e);
      setToast({ type: "error", message: "No se pudieron cargar los extras." });
      setRows([]);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    load();
    // eslint-disable-next-line
  }, [open, osId]);

  const startEdit = (r) => {
    setEditingId(r.id);
    setForm({
      description: r.description || "",
      qty: num(r.qty ?? 1, 1),
      price: num(r.price ?? 0, 0),
      currency: r.currency || "USD BNA",
      note: r.note || "",
    });
  };

  const save = async () => {
    if (!osId) return;
    const desc = String(form.description || "").trim();
    if (!desc) {
      setToast({ type: "warning", message: "La descripción es requerida." });
      return;
    }

    const payload = {
      os_id: osId,
      description: desc,
      qty: num(form.qty ?? 1, 1),
      price: num(form.price ?? 0, 0),
      currency: String(form.currency || "USD BNA"),
      note: String(form.note || "").trim() || null,
      updated_at: new Date().toISOString(),
    };

    try {
      setSaving(true);
      if (editingId) {
        const { error } = await supabase
          .from("service_price_extras")
          .update(payload)
          .eq("id", editingId);
        if (error) throw error;
        setToast({ type: "success", message: "Extra actualizado." });
      } else {
        const { error } = await supabase.from("service_price_extras").insert([payload]);
        if (error) throw error;
        setToast({ type: "success", message: "Extra agregado." });
      }

      await load();
      resetForm();
      onChanged?.();
    } catch (e) {
      console.error("[ExtrasModal] save", e);
      setToast({ type: "error", message: "No se pudo guardar el extra." });
    } finally {
      setSaving(false);
    }
  };

  const remove = async (id) => {
    if (!id) return;
    const ok = window.confirm("¿Eliminar este extra?");
    if (!ok) return;

    try {
      setSaving(true);
      const { error } = await supabase.from("service_price_extras").delete().eq("id", id);
      if (error) throw error;
      setToast({ type: "success", message: "Extra eliminado." });
      await load();
      onChanged?.();
    } catch (e) {
      console.error("[ExtrasModal] remove", e);
      setToast({ type: "error", message: "No se pudo eliminar el extra." });
    } finally {
      setSaving(false);
    }
  };

  if (!open) return null;

  return (
    <div className="fixed inset-0 z-[120] flex items-center justify-center bg-black/70 p-4">
      <div className="w-full max-w-5xl bg-neutral-900 text-white rounded-xl shadow-2xl border border-white/10">
        <div className="px-5 py-4 border-b border-white/10 flex items-center justify-between">
          <div>
            <div className="text-lg font-semibold">Extras de facturación</div>
            <div className="text-xs text-slate-300">
              {osCode ? `OS: ${osCode}` : osId}
            </div>
          </div>
          <div className="flex items-center gap-2">
            <div className="text-right">
              <div className="text-xs text-slate-300">Total extras</div>
              <div className="text-lg font-semibold">{fmt(totalExtras)}</div>
            </div>
            <button
              onClick={() => {
                resetForm();
                onClose?.();
              }}
              className="px-3 py-2 rounded-md bg-white/10 hover:bg-white/20 text-sm"
            >
              Cerrar
            </button>
          </div>
        </div>

        <div className="p-5 grid grid-cols-1 lg:grid-cols-12 gap-4">
          <div className="lg:col-span-7 rounded-lg border border-white/10 bg-black/20 p-3">
            <div className="text-sm font-semibold mb-2">Listado</div>

            {loading ? (
              <div className="text-xs text-white/70">Cargando…</div>
            ) : rows.length === 0 ? (
              <div className="text-xs text-white/70">No hay extras cargados.</div>
            ) : (
              <div className="space-y-2 max-h-[420px] overflow-auto pr-1">
                {rows.map((r) => (
                  <div
                    key={r.id}
                    className="rounded-md border border-white/10 bg-white/5 p-3 flex items-start justify-between gap-3"
                  >
                    <div className="min-w-0">
                      <div className="font-semibold">{r.description}</div>
                      {r.note ? <div className="text-xs text-white/70 mt-0.5">{r.note}</div> : null}
                      <div className="text-xs text-white/70 mt-2">
                        {fmt(r.qty)} × {fmt(r.price)} {r.currency || "USD BNA"}{" "}
                        <span className="text-white/50">=</span>{" "}
                        <span className="text-white">{fmt(num(r.qty,0) * num(r.price,0))}</span>
                      </div>
                    </div>

                    <div className="flex gap-2 shrink-0">
                      <button
                        onClick={() => startEdit(r)}
                        className="px-2 py-1 rounded bg-white/10 hover:bg-white/20 text-xs border border-white/10"
                      >
                        Editar
                      </button>
                      <button
                        onClick={() => remove(r.id)}
                        disabled={saving}
                        className="px-2 py-1 rounded bg-red-700/30 hover:bg-red-700/40 text-xs border border-red-600/30 text-red-200"
                      >
                        Eliminar
                      </button>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>

          <div className="lg:col-span-5 rounded-lg border border-white/10 bg-black/20 p-3">
            <div className="flex items-center justify-between">
              <div className="text-sm font-semibold">
                {editingId ? "Editar extra" : "Agregar extra"}
              </div>
              {editingId ? (
                <button
                  onClick={resetForm}
                  className="text-xs text-white/70 hover:text-white underline"
                >
                  cancelar edición
                </button>
              ) : null}
            </div>

            <div className="mt-3 space-y-3">
              <div>
                <div className="text-xs text-white/70">Descripción</div>
                <input
                  className="w-full rounded bg-white/10 border border-white/20 p-2 text-sm"
                  value={form.description}
                  onChange={(e) => setForm((p) => ({ ...p, description: e.target.value }))}
                  placeholder="Ej: In Situ - Calibración / Cambio de baterías"
                />
              </div>

              <div className="grid grid-cols-3 gap-2">
                <div>
                  <div className="text-xs text-white/70">Cantidad</div>
                  <input
                    type="number"
                    step="0.01"
                    className="w-full rounded bg-white/10 border border-white/20 p-2 text-sm text-right"
                    value={form.qty}
                    onChange={(e) => setForm((p) => ({ ...p, qty: e.target.value }))}
                  />
                </div>
                <div>
                  <div className="text-xs text-white/70">Precio</div>
                  <input
                    type="number"
                    step="0.01"
                    className="w-full rounded bg-white/10 border border-white/20 p-2 text-sm text-right"
                    value={form.price}
                    onChange={(e) => setForm((p) => ({ ...p, price: e.target.value }))}
                  />
                </div>
                <div>
                  <div className="text-xs text-white/70">Moneda</div>
                  <select
                    className="w-full rounded bg-white/10 border border-white/20 p-2 text-sm"
                    value={form.currency}
                    onChange={(e) => setForm((p) => ({ ...p, currency: e.target.value }))}
                  >
                    <option value="USD BNA">USD BNA</option>
                    <option value="ARS">ARS</option>
                    <option value="EUR">EUR</option>
                  </select>
                </div>
              </div>

              <div>
                <div className="text-xs text-white/70">Nota (opcional)</div>
                <input
                  className="w-full rounded bg-white/10 border border-white/20 p-2 text-sm"
                  value={form.note}
                  onChange={(e) => setForm((p) => ({ ...p, note: e.target.value }))}
                  placeholder="Ej: 2 técnicos / repuesto incluido"
                />
              </div>

              <div className="flex gap-2 pt-1">
                <button
                  onClick={save}
                  disabled={saving}
                  className="px-3 py-2 rounded-md bg-emerald-600 hover:bg-emerald-500 text-xs"
                >
                  {saving ? "Guardando…" : editingId ? "Guardar cambios" : "Agregar"}
                </button>
                <button
                  onClick={resetForm}
                  disabled={saving}
                  className="px-3 py-2 rounded-md bg-white/10 hover:bg-white/20 text-xs border border-white/10"
                >
                  Limpiar
                </button>
              </div>

              <div className="text-[11px] text-white/50">
                Estos extras se guardan en <span className="font-mono">service_price_extras</span> por OS.
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}

function normalizeRemitoManagerRow(remito = {}, index = 0) {
  return {
    id: remito.id || genLocalId("remito"),
    persisted_id: remito.id || null,
    official_number: remito.official_number || remito.code || "",
    date_out: remito.date_out ? String(remito.date_out).slice(0, 10) : todayInputYMD(),
    notes: remito.notes || "",
    pdf_url: remito.pdf_url || "",
    file_name: remito.file_name || fileNameFromStorageUrl(remito.pdf_url),
    file_type: remito.file_type || (remito.pdf_url ? "application/pdf" : ""),
    os_code: remito.os_code || "",
    created_at: remito.created_at || null,
    file: null,
    sort_index: index,
  };
}

function remitoFileLabel(row) {
  if (row?.file?.name) return row.file.name;
  if (row?.file_name) return row.file_name;
  if (row?.pdf_url) return "Archivo cargado";
  return "Sin archivo";
}

function RemitoManagerModal({
  open,
  onClose,
  remitos,
  osId,
  poId,
  clientId,
  osCode,
  onSaved,
}) {
  const [rows, setRows] = useState([]);
  const [saving, setSaving] = useState(false);
  const [previewRowId, setPreviewRowId] = useState("");
  const [previewSrc, setPreviewSrc] = useState("");
  const [previewKind, setPreviewKind] = useState("");
  const [previewName, setPreviewName] = useState("");

  useEffect(() => {
    if (!open) return;
    const nextRows = (remitos || []).length
      ? (remitos || []).map((row, index) => normalizeRemitoManagerRow(row, index))
      : [normalizeRemitoManagerRow({}, 0)];
    setRows(nextRows);
    setPreviewRowId(nextRows[0]?.id || "");
  }, [open, remitos]);

  useEffect(() => {
    if (!open) {
      setPreviewSrc("");
      setPreviewKind("");
      setPreviewName("");
      return undefined;
    }

    const row = rows.find((item) => item.id === previewRowId) || rows[0] || null;
    if (!row) {
      setPreviewSrc("");
      setPreviewKind("");
      setPreviewName("");
      return undefined;
    }

    let objectUrl = "";
    const source = row.file ? (objectUrl = URL.createObjectURL(row.file)) : row.pdf_url || "";
    setPreviewSrc(source);
    setPreviewKind(guessFileKind(row));
    setPreviewName(row.file?.name || row.file_name || row.official_number || "Vista previa");

    return () => {
      if (objectUrl) URL.revokeObjectURL(objectUrl);
    };
  }, [open, rows, previewRowId]);

  const updateRow = (id, patch) => {
    setRows((prev) => prev.map((row) => (row.id === id ? { ...row, ...patch } : row)));
  };

  const addRow = () => {
    setRows((prev) => [...prev, normalizeRemitoManagerRow({}, prev.length)]);
  };

  const removeRow = (id) => {
    setRows((prev) => {
      if (prev.length <= 1) return prev;
      const next = prev.filter((row) => row.id !== id);
      if (previewRowId === id) setPreviewRowId(next[0]?.id || "");
      return next;
    });
  };

  const onFileChange = (id, file) => {
    updateRow(id, {
      file: file || null,
      file_name: file?.name || "",
      file_type: file?.type || "",
    });
    setPreviewRowId(id);
  };

  const uploadRemitoFile = async (remitoId, file) => {
    const path = `${osId}/${remitoId}/${Date.now()}-${safeFileName(file.name)}`;
    const { error: upErr } = await supabase.storage
      .from("remitos")
      .upload(path, file, {
        contentType: file.type || undefined,
        upsert: false,
      });
    if (upErr) throw upErr;

    const { data: pub } = supabase.storage.from("remitos").getPublicUrl(path);
    const publicUrl = pub?.publicUrl;
    if (!publicUrl) throw new Error("No se pudo obtener la URL pública del remito.");
    return publicUrl;
  };

  const save = async () => {
    if (!osId) return;
    if (!clientId) {
      alert("No se pudo resolver el cliente para guardar remitos.");
      return;
    }

    setSaving(true);
    try {
      const existingIds = new Set((remitos || []).map((row) => row.id).filter(Boolean));
      const keptExistingIds = new Set(rows.map((row) => row.persisted_id).filter(Boolean));
      const deletedIds = [...existingIds].filter((id) => !keptExistingIds.has(id));

      for (const deletedId of deletedIds) {
        const { data: itemRows, error: itemsErr } = await supabase
          .from("remito_items")
          .select("id")
          .eq("remito_id", deletedId)
          .limit(1);
        if (itemsErr) throw itemsErr;
        if ((itemRows || []).length > 0) {
          throw new Error("No se puede eliminar un remito que tiene instrumentos asociados.");
        }
        const { error: delErr } = await supabase.from("remitos").delete().eq("id", deletedId);
        if (delErr) throw delErr;
      }

      for (let i = 0; i < rows.length; i += 1) {
        const row = rows[i];
        const officialNumber = String(row.official_number || "").trim();
        if (!officialNumber) throw new Error(`El remito ${i + 1} necesita un número.`);
        if (!row.file && !row.pdf_url) {
          throw new Error(`El remito ${i + 1} necesita un archivo PDF o imagen.`);
        }

        let remitoId = row.persisted_id;
        if (!remitoId) {
          const { data, error } = await supabase
            .from("remitos")
            .insert({
              os_id: osId,
              client_id: clientId,
              code: officialNumber,
              official_number: officialNumber,
              date_out: row.date_out || todayInputYMD(),
              notes: row.notes?.trim() || null,
              status: "borrador",
            })
            .select("id")
            .single();
          if (error) throw error;
          remitoId = data?.id;
        }

        let pdfUrl = row.pdf_url || null;
        if (row.file) {
          pdfUrl = await uploadRemitoFile(remitoId, row.file);
        }

        const { error: updErr } = await supabase
          .from("remitos")
          .update({
            code: officialNumber,
            official_number: officialNumber,
            date_out: row.date_out || todayInputYMD(),
            notes: row.notes?.trim() || null,
            pdf_url: pdfUrl,
            updated_at: new Date().toISOString(),
          })
          .eq("id", remitoId);
        if (updErr) throw updErr;
      }

      await onSaved?.();
      onClose?.();
    } catch (e) {
      console.error("[RemitoManagerModal] save:", e);
      alert(e?.message || "No se pudieron guardar los remitos.");
    } finally {
      setSaving(false);
    }
  };

  const selectedRow = rows.find((item) => item.id === previewRowId) || rows[0] || null;

  if (!open) return null;

  return (
    <div className="fixed inset-0 z-[112] flex items-center justify-center bg-black/80 p-4 backdrop-blur-sm">
      <div className="flex max-h-[92vh] w-full max-w-4xl flex-col overflow-hidden rounded-2xl border border-white/20 bg-neutral-950 text-white shadow-2xl">
        <div className="flex items-start justify-between gap-4 border-b border-white/20 px-5 py-4">
          <div>
            <h3 className="text-lg font-semibold">Remitos escaneados</h3>
            <p className="mt-1 text-xs text-white/55">
              {osCode || "Orden de servicio"} · {poId ? "OC vinculada" : "Sin OC"}
            </p>
          </div>
          <button
            type="button"
            onClick={onClose}
            className="h-9 rounded-md border border-orange-500/40 px-3 text-sm text-orange-300 hover:bg-orange-500/20"
            disabled={saving}
          >
            Cerrar
          </button>
        </div>

        <div className="max-h-[calc(92vh-140px)] overflow-auto p-5 space-y-4">
          <div className="rounded-xl border border-white/70 bg-black/20 px-4 py-3 text-xs text-white/55">
            Cada bloque guarda número de remito y archivo asociado. Podés cargar varios remitos para la misma OC o centro de costo.
          </div>

          <div className="space-y-3">
            {rows.map((row, index) => (
              <div key={row.id} className="rounded-xl border border-white/70 bg-neutral-950 p-4">
                <div className="flex items-center justify-between gap-3">
                  <div className="text-sm font-medium">Remito {index + 1}</div>
                  <div className="flex items-center gap-2">
                    <button
                      type="button"
                      onClick={() => setPreviewRowId(row.id)}
                      className={`rounded-md border px-2 py-1 text-xs ${
                        previewRowId === row.id
                          ? "border-orange-500/50 bg-orange-500/20 text-orange-300"
                          : "border-orange-500/40 text-orange-300 hover:bg-orange-500/10"
                      }`}
                    >
                      Previsualizar
                    </button>
                    <button
                      type="button"
                      onClick={() => removeRow(row.id)}
                      disabled={rows.length <= 1}
                      className="text-xs text-rose-300 hover:text-rose-200 disabled:opacity-40"
                    >
                      Eliminar
                    </button>
                  </div>
                </div>

                <div className="mt-4 grid gap-3 md:grid-cols-2">
                  <div>
                    <label className="mb-1 block text-xs text-white/55">Nº de remito</label>
                    <input
                      className="w-full rounded border border-white/15 bg-white px-3 py-2 text-sm text-slate-900"
                      value={row.official_number}
                      onChange={(e) => updateRow(row.id, { official_number: e.target.value })}
                      placeholder="Ej: 0001-00005623"
                    />
                  </div>
                  <div>
                    <label className="mb-1 block text-xs text-white/55">Fecha salida</label>
                    <input
                      type="date"
                      className="w-full rounded border border-white/15 bg-white px-3 py-2 text-sm text-slate-900"
                      value={row.date_out}
                      onChange={(e) => updateRow(row.id, { date_out: e.target.value })}
                    />
                  </div>
                  <div className="md:col-span-2">
                    <label className="mb-1 block text-xs text-white/55">Archivo PDF / JPG / PNG</label>
                    <input
                      type="file"
                      accept=".pdf,.png,.jpg,.jpeg,application/pdf,image/png,image/jpeg"
                      className="w-full rounded border border-white/15 bg-white px-3 py-2 text-sm text-slate-900 file:mr-4 file:rounded-md file:border-0 file:bg-slate-100 file:px-3 file:py-1 file:text-blue-600"
                      onChange={(e) => onFileChange(row.id, e.target.files?.[0] || null)}
                    />
                    <div className="mt-1 text-[11px] text-white/45">{remitoFileLabel(row)}</div>
                  </div>
                  <div className="md:col-span-2">
                    <label className="mb-1 block text-xs text-white/55">Nota</label>
                    <input
                      className="w-full rounded border border-white/15 bg-white px-3 py-2 text-sm text-slate-900"
                      value={row.notes}
                      onChange={(e) => updateRow(row.id, { notes: e.target.value })}
                      placeholder="Opcional"
                    />
                  </div>
                </div>

                <div className="mt-3 flex flex-wrap items-center gap-2 text-xs text-white/45">
                  {row.pdf_url ? (
                    <a
                      href={row.pdf_url}
                      target="_blank"
                      rel="noreferrer"
                      className="rounded-md border border-orange-500/40 px-2 py-1 text-orange-300 hover:bg-orange-500/20"
                    >
                      Ver archivo actual
                    </a>
                  ) : null}
                  {row.file_type ? <span>{row.file_type}</span> : null}
                  {row.os_code ? <span>OS: {row.os_code}</span> : null}
                </div>
              </div>
            ))}
          </div>

          <div className="rounded-xl border border-white/70 bg-neutral-950 p-4">
            <div className="flex items-center justify-between gap-3">
              <div>
                <div className="text-sm font-medium">Vista previa</div>
                <div className="text-xs text-white/55">
                  {selectedRow
                    ? `Remito ${rows.findIndex((item) => item.id === selectedRow.id) + 1} · ${previewName}`
                    : "Seleccioná un remito para previsualizar"}
                </div>
              </div>
              <div className="text-xs text-white/55">
                {previewKind === "pdf" ? "PDF" : previewKind === "image" ? "Imagen" : previewKind || "Sin archivo"}
              </div>
            </div>
            <div className="mt-4 overflow-hidden rounded-lg border border-white/70 bg-black">
              {previewSrc ? (
                previewKind === "image" ? (
                  <img
                    src={previewSrc}
                    alt={previewName || "Vista previa de remito"}
                    className="max-h-[420px] w-full bg-black object-contain"
                  />
                ) : (
                  <iframe
                    title={previewName || "Vista previa de remito"}
                    src={previewSrc}
                    className="h-[420px] w-full border-0 bg-white"
                  />
                )
              ) : (
                <div className="flex h-[240px] items-center justify-center text-sm text-white/55">
                  No hay archivo para previsualizar.
                </div>
              )}
            </div>
          </div>

          <div className="flex items-center justify-between gap-3">
            <button
              type="button"
              onClick={addRow}
              className="rounded-lg border border-orange-500/40 px-4 py-2 text-sm text-orange-300 hover:bg-orange-500/20"
            >
              Agregar remito
            </button>
            <div className="text-xs text-white/45">
              Si el remito ya existe pero cambió el archivo, podés volver a subirlo en el mismo bloque.
            </div>
          </div>
        </div>

        <div className="flex items-center justify-end gap-2 border-t border-white/20 px-5 py-4">
          <button
            type="button"
            onClick={onClose}
            className="rounded-lg border border-orange-500/40 px-4 py-2 text-sm text-orange-300 hover:bg-orange-500/20"
            disabled={saving}
          >
            Cancelar
          </button>
          <button
            type="button"
            onClick={save}
            className="rounded-lg bg-orange-500 px-4 py-2 text-sm font-semibold text-neutral-950 hover:bg-orange-400 disabled:opacity-60"
            disabled={saving}
          >
            {saving ? "Guardando..." : "Guardar"}
          </button>
        </div>
      </div>
    </div>
  );
}

export default function ServicePricingModal(props) {
  const {
    open: openProp,
    isOpen: isOpenProp,
    offerId,
    osId: osIdProp,
    onClose,
    onUpdated,
    setToast = () => {},
  } = props;

  const visible = openProp ?? isOpenProp ?? false;

  const [resolvedOsId, setResolvedOsId] = useState(null);
  const effectiveOsId = osIdProp ?? resolvedOsId;
  const [osNotFound, setOsNotFound] = useState(false);

  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [autoSaveState, setAutoSaveState] = useState("idle");
  const [autoSaveMessage, setAutoSaveMessage] = useState("");
  const [activePricingTab, setActivePricingTab] = useState("prices");
  const [facturando, setFacturando] = useState(false);
  const [rows, setRows] = useState([]);
  const [header, setHeader] = useState(null);
  const [error, setError] = useState(null);

  const [generalObservations, setGeneralObservations] = useState("");
  const [ocNumber, setOcNumber] = useState(null);
  const [ocPdfUrl, setOcPdfUrl] = useState(null);
  const [billingOcDocuments, setBillingOcDocuments] = useState([]);
  const [ocViewerOpen, setOcViewerOpen] = useState(false);

  // ---------------- FACTURAS (multi por PO) ----------------
  const [poId, setPoId] = useState(null);

  const [invoices, setInvoices] = useState([]);
  const [currentInvoiceId, setCurrentInvoiceId] = useState(null);
  const [invoiceItems, setInvoiceItems] = useState([]);

  // instrument_id -> Set(invoice_id)
  const [invoicedMap, setInvoicedMap] = useState(new Map());
  // extra_id -> Set(invoice_id)
  const [invoicedExtraMap, setInvoicedExtraMap] = useState(new Map());

  const [loadingInvoices, setLoadingInvoices] = useState(false);
  const [savingInvoice, setSavingInvoice] = useState(false);
  const [invoiceError, setInvoiceError] = useState(null);
  const [invoiceNumberEditable, setInvoiceNumberEditable] = useState(false);
  const [invoiceStatusEditable, setInvoiceStatusEditable] = useState(false);
  const [requestedAtEditable, setRequestedAtEditable] = useState(false);
  const [issuedAtEditable, setIssuedAtEditable] = useState(false);
  const [refactureAllowedInstrumentIds, setRefactureAllowedInstrumentIds] = useState(
    () => new Set()
  );
  const [refactureAllowedExtraIds, setRefactureAllowedExtraIds] = useState(() => new Set());

  const [invoiceForm, setInvoiceForm] = useState({
    requested_at: "",
    invoice_number: "",
    issued_at: "",
    status: "draft",
    note: "",
  });

  // ---------------- REMITOS (escaneados) ----------------
  const [remitos, setRemitos] = useState([]);
  const [loadingRemitos, setLoadingRemitos] = useState(false);
  const [uploadingRemitoId, setUploadingRemitoId] = useState(null);
  const [remitoManagerOpen, setRemitoManagerOpen] = useState(false);
  const [creatingRemito, setCreatingRemito] = useState(false);
  const [showNewRemitoForm, setShowNewRemitoForm] = useState(false);
  const [newRemitoNumber, setNewRemitoNumber] = useState("");
  const [newRemitoDate, setNewRemitoDate] = useState(() => todayInputYMD());
  const [newRemitoNotes, setNewRemitoNotes] = useState("");
  const [newRemitoFile, setNewRemitoFile] = useState(null);
  const [remitoContext, setRemitoContext] = useState({ clientId: null, osCode: "" });
  const [remitosError, setRemitosError] = useState(null);

  // ---------------- EXTRAS (ítems adicionales por OS) ----------------
  const [extrasOpen, setExtrasOpen] = useState(false);
  const [extrasRows, setExtrasRows] = useState([]);
  const [loadingExtras, setLoadingExtras] = useState(false);
  const [extrasError, setExtrasError] = useState(null);

  const loadExtras = async (osId) => {
    if (!osId) return;
    try {
      setLoadingExtras(true);
      setExtrasError(null);

      const { data, error } = await supabase
        .from("service_price_extras")
        .select("id,os_id,description,qty,price,currency,note,created_at")
        .eq("os_id", osId)
        .order("created_at", { ascending: true });

      if (error) throw error;
      setExtrasRows(data || []);
    } catch (e) {
      console.error("[loadExtras]", e);
      setExtrasError(e.message || "No se pudieron cargar los extras.");
      setExtrasRows([]);
    } finally {
      setLoadingExtras(false);
    }
  };

  const fileInputRef = useRef(null);
  const pendingRemitoIdRef = useRef(null);
  const originalRowsRef = useRef(new Map());
  const lastSavedPricingSignatureRef = useRef("");
  const autoSaveTimerRef = useRef(null);

  const ordered = useMemo(
    () =>
      [...rows].sort((a, b) =>
        String(a.serv_nro).localeCompare(String(b.serv_nro))
      ),
    [rows]
  );

  const total = useMemo(
    () => ordered.reduce((acc, r) => acc + num(r.subtotal), 0),
    [ordered]
  );


  const extrasTotal = useMemo(
    () => (extrasRows || []).reduce((acc, r) => acc + num(r.qty, 0) * num(r.price, 0), 0),
    [extrasRows]
  );

  const grandTotal = useMemo(() => total + extrasTotal, [total, extrasTotal]);


  const remitosConArchivo = useMemo(
    () => (remitos || []).filter((r) => !!r.pdf_url),
    [remitos]
  );

  const resetNewRemitoForm = () => {
    setNewRemitoNumber("");
    setNewRemitoDate(todayInputYMD());
    setNewRemitoNotes("");
    setNewRemitoFile(null);
  };

  const loadRemitos = async (osId, currentPoId = poId) => {
    if (!osId) return;
    try {
      setLoadingRemitos(true);
      setRemitosError(null);

      let serviceOrders = [];
      if (currentPoId) {
        const { data: soRows, error: soListErr } = await supabase
          .from("service_orders")
          .select("id, code")
          .eq("po_id", currentPoId)
          .order("code", { ascending: true });
        if (soListErr) throw soListErr;
        serviceOrders = soRows || [];
      }

      if (!serviceOrders.length) {
        const { data: soRow, error: soErr } = await supabase
          .from("service_orders")
          .select("id, code")
          .eq("id", osId)
          .maybeSingle();
        if (soErr) throw soErr;
        serviceOrders = soRow?.id ? [soRow] : [{ id: osId, code: "" }];
      }

      const osIds = serviceOrders.map((row) => row.id).filter(Boolean);
      const osCodeById = new Map(serviceOrders.map((row) => [row.id, row.code || ""]));
      if (!osIds.length) {
        setRemitos([]);
        return;
      }

      const { data, error } = await supabase
        .from("remitos")
        .select("id, os_id, code, official_number, date_out, status, pdf_url, notes, created_at")
        .in("os_id", osIds)
        .order("created_at", { ascending: true });

      if (error) throw error;
      setRemitos((data || []).map((row) => ({ ...row, os_code: osCodeById.get(row.os_id) || "" })));
    } catch (e) {
      console.error("[loadRemitos]", e);
      setRemitosError(e.message || "No se pudieron cargar los remitos.");
      setRemitos([]);
    } finally {
      setLoadingRemitos(false);
    }
  };

  const createScannedRemito = async () => {
    if (!effectiveOsId) return;

    const officialNumber = newRemitoNumber.trim();
    if (!officialNumber) {
      setRemitosError("Ingresá el número de remito.");
      return;
    }
    if (!newRemitoFile) {
      setRemitosError("Seleccioná el PDF o imagen del remito.");
      return;
    }
    if (!remitoContext.clientId) {
      setRemitosError("No se pudo resolver el cliente de la OC para crear el remito.");
      return;
    }

    let insertedId = null;
    try {
      setCreatingRemito(true);
      setRemitosError(null);

      const { data: inserted, error: insertErr } = await supabase
        .from("remitos")
        .insert({
          os_id: effectiveOsId,
          code: officialNumber,
          official_number: officialNumber,
          date_out: newRemitoDate || todayInputYMD(),
          client_id: remitoContext.clientId,
          notes: newRemitoNotes.trim() || null,
          status: "borrador",
        })
        .select("id")
        .single();

      if (insertErr) throw insertErr;
      insertedId = inserted?.id;
      if (!insertedId) throw new Error("No se pudo crear el remito.");

      const path = `${effectiveOsId}/${insertedId}/${Date.now()}-${safeFileName(
        newRemitoFile.name
      )}`;

      const { error: upErr } = await supabase.storage
        .from("remitos")
        .upload(path, newRemitoFile, {
          contentType: newRemitoFile.type || undefined,
          upsert: false,
        });
      if (upErr) throw upErr;

      const { data: pub } = supabase.storage.from("remitos").getPublicUrl(path);
      const publicUrl = pub?.publicUrl;
      if (!publicUrl) throw new Error("No se pudo obtener la URL pública del remito.");

      const { error: updErr } = await supabase
        .from("remitos")
        .update({ pdf_url: publicUrl, updated_at: new Date().toISOString() })
        .eq("id", insertedId);
      if (updErr) throw updErr;

      resetNewRemitoForm();
      setShowNewRemitoForm(false);
      await loadRemitos(effectiveOsId, poId);
    } catch (e) {
      console.error("[createScannedRemito]", e);
      if (insertedId) {
        await supabase.from("remitos").delete().eq("id", insertedId);
      }
      setRemitosError(e.message || "No se pudo crear el remito escaneado.");
    } finally {
      setCreatingRemito(false);
    }
  };

  const refreshBillingModal = async () => {
    const activeInvoiceId = currentInvoiceId;
    const tasks = [];

    if (effectiveOsId) {
      tasks.push(loadRemitos(effectiveOsId, poId));
      tasks.push(loadExtras(effectiveOsId));
    }

    if (poId) {
      tasks.push(loadInvoices(poId));
    }

    await Promise.allSettled(tasks);

    if (activeInvoiceId) {
      await loadInvoiceItems(activeInvoiceId);
    }
  };

  const openUploadForRemito = (remitoId) => {
    pendingRemitoIdRef.current = remitoId;
    if (fileInputRef.current) fileInputRef.current.value = "";
    fileInputRef.current?.click();
  };

  const handleFileSelected = async (e) => {
    const file = e.target.files?.[0];
    const remitoId = pendingRemitoIdRef.current;
    if (!file || !remitoId) return;

    try {
      setUploadingRemitoId(remitoId);
      setRemitosError(null);

      const ext = safeFileName(file.name).split(".").pop() || "pdf";
      const path = `${effectiveOsId}/${remitoId}/${Date.now()}-${safeFileName(
        file.name
      )}`;

      const { error: upErr } = await supabase.storage
        .from("remitos")
        .upload(path, file, {
          contentType: file.type || undefined,
          upsert: false,
        });

      if (upErr) throw upErr;

      const { data: pub } = supabase.storage
        .from("remitos")
        .getPublicUrl(path);
      const publicUrl = pub?.publicUrl;
      if (!publicUrl)
        throw new Error("No se pudo obtener la URL pública del remito.");

      const { error: updErr } = await supabase
        .from("remitos")
        .update({ pdf_url: publicUrl, updated_at: new Date().toISOString() })
        .eq("id", remitoId);

      if (updErr) throw updErr;

      await loadRemitos(effectiveOsId, poId);
    } catch (e) {
      console.error("[uploadRemitoScan]", e);
      setRemitosError(e.message || "No se pudo subir el remito.");
    } finally {
      setUploadingRemitoId(null);
    }
  };

  // --- Resolver OS usando RPC seguro ---
  useEffect(() => {
    let cancel = false;

    async function resolveOsIdByBaseNumber(baseNumber) {
      if (!baseNumber) return null;
      try {
        const { data: osRows, error: osErr } = await supabase
          .from("service_orders")
          .select("id, base_number, created_at")
          .eq("base_number", baseNumber)
          .order("created_at", { ascending: false })
          .limit(50);

        if (osErr) throw osErr;
        const candidates = (osRows || []).filter((r) => !!r.id);
        if (!candidates.length) return null;
        if (candidates.length === 1) return candidates[0].id;

        const ids = candidates.map((x) => x.id);
        const { data: prices, error: pErr } = await supabase
          .from("service_prices")
          .select("os_id")
          .in("os_id", ids);
        if (pErr) throw pErr;

        const score = new Map();
        for (const id of ids) score.set(id, 0);
        for (const p of prices || []) {
          if (!p?.os_id) continue;
          score.set(p.os_id, (score.get(p.os_id) || 0) + 1);
        }

        const ranked = [...candidates].sort((a, b) => {
          const sa = score.get(a.id) || 0;
          const sb = score.get(b.id) || 0;
          if (sb !== sa) return sb - sa;
          return String(b.created_at || "").localeCompare(String(a.created_at || ""));
        });

        return ranked[0]?.id || null;
      } catch (err) {
        console.warn("[resolveOsIdByBaseNumber]", err?.message || err);
        return null;
      }
    }

    async function resolveOs() {
      if (osIdProp || !visible) return;

      try {
        setError(null);
        if (!offerId) {
          setResolvedOsId(null);
          return;
        }

        const { data, error } = await supabase.rpc("get_os_id_for_offer", {
          p_offer_id: offerId,
        });
        const row = Array.isArray(data) ? data[0] : data;
        const rpcOsId = row?.os_id || null;

        const { data: offerRow } = await supabase
          .from("offers")
          .select("base_number, code")
          .eq("id", offerId)
          .maybeSingle();

        const baseNumber =
          Number.isFinite(Number(offerRow?.base_number))
            ? Number(offerRow.base_number)
            : extractBaseNumber(offerRow?.code);

        const fallbackOsId = await resolveOsIdByBaseNumber(baseNumber);
        const finalOsId = fallbackOsId || rpcOsId;

        if (error) {
          throw new Error("Error al consultar la Orden de Servicio en la base de datos.");
        }

        if (!finalOsId) {
          // Oferta sin OS vinculada — intentar obtener el PO directo para facturación
          const { data: directPo } = await supabase
            .from("purchase_orders")
            .select("id")
            .eq("offer_id", offerId)
            .maybeSingle();
          if (!cancel) {
            if (directPo?.id) setPoId(directPo.id);
            setOsNotFound(true);
          }
          return;
        }

        if (!cancel) setResolvedOsId(finalOsId);
      } catch (err) {
        console.error("[resolveOs]", err);
        if (!cancel) setError(err.message || "Error al resolver OS.");
      }
    }

    resolveOs();
    return () => (cancel = true);
  }, [visible, offerId, osIdProp]);

  // --- Cargar datos ---
  useEffect(() => {
    let cancel = false;

    if (!visible || !effectiveOsId) return;
    setActivePricingTab("prices");

    async function loadRows() {
      try {
        setLoading(true);
        setBillingOcDocuments([]);

        // Header
        let hdr = null;
        try {
          const { data } = await supabase.rpc("get_billing_request", {
            p_os_id: effectiveOsId,
          });
          hdr = Array.isArray(data) ? data[0] : null;
        } catch {}

        const { data: soRow, error: soErr } = await supabase
          .from("service_orders")
          .select("id, code, po_id, general_observations")
          .eq("id", effectiveOsId)
          .single();
        if (soErr) throw soErr;

        let poClientId = null;
        if (soRow?.po_id) {
          const { data: poContext } = await supabase
            .from("purchase_orders")
            .select("client_id")
            .eq("id", soRow.po_id)
            .maybeSingle();
          poClientId = poContext?.client_id || null;
        }

        // 🔐 OC por RPC seguro
        let ocNum = null;
        let ocPdf = null;

        try {
          const { data } = await supabase.rpc("get_offer_for_os", {
            p_os_id: effectiveOsId,
          });
          const row = Array.isArray(data) ? data[0] : null;
          if (row) {
            ocNum = row.oc_number || row.accept_reference || null;
            ocPdf = row.oc_pdf_url || null;
          }
        } catch {
          // fallback legacy
          if (soRow?.po_id) {
            const { data: poRow } = await supabase
              .from("purchase_orders")
              .select("offer_id")
              .eq("id", soRow.po_id)
              .single();

            if (poRow?.offer_id) {
              const { data: offerRow } = await supabase
                .from("offers")
                .select("oc_number, accept_reference, oc_pdf_url")
                .eq("id", poRow.offer_id)
                .single();

              ocNum = offerRow?.oc_number || offerRow?.accept_reference || null;
              ocPdf = offerRow?.oc_pdf_url || null;
            }
          }
        }

        const attachmentCollections = await loadBillingAttachments({
          osId: effectiveOsId,
          poId: soRow?.po_id || null,
          offerId: offerId || null,
          fallbackOcUrl: ocPdf,
          fallbackOcNumber: ocNum,
        });

        const { data: instRows, error: instErr } = await supabase
          .from("v_service_orders_enriched")
          .select(
            "po_id,instrument_id,code,instrument_tag,instrument_brand,instrument_model,instrument_serial"
          )
          .eq("po_id", soRow.po_id)
          .order("code", { ascending: true });
        if (instErr) throw instErr;

        const ids = [
          ...new Set((instRows || []).map((r) => r.instrument_id).filter(Boolean)),
        ];

        // Obtener baker_code y categoría en una sola consulta
        const { data: instExtra } = ids.length
          ? await supabase
              .from("instruments")
              .select("id, baker_code, instrument_categories(name)")
              .in("id", ids)
          : { data: [] };

        const bakerMap = new Map((instExtra || []).map((i) => [i.id, i.baker_code]));
        const categoryMap = new Map(
          (instExtra || []).map((i) => [i.id, i.instrument_categories?.name || null])
        );

        const { data: prices } = await supabase
          .from("service_prices")
          .select("id,os_id,instrument_id,qty,price,note")
          .eq("os_id", effectiveOsId);

        const priceMap = new Map((prices || []).map((p) => [p.instrument_id, p]));

        const merged = (instRows || []).map((i) => {
          const p = priceMap.get(i.instrument_id);
          const qty = num(p?.qty ?? 1, 1);
          const pu = num(p?.price ?? 0, 0);

          return {
            instrument_id: i.instrument_id,
            service_price_id: p?.id ?? null,
            centro_cto: deriveCentroCtoFromCode(i.code),
            serv_nro: normalizeServNro(i.code),
            descripcion: buildDescription({
              ...i,
              baker_code: bakerMap.get(i.instrument_id),
              instrument_category: categoryMap.get(i.instrument_id),
            }),
            qty,
            price: pu,
            currency: "USD BNA",
            subtotal: qty * pu,
            note: p?.note ?? "",
          };
        });

        if (cancel) return;

        setHeader(hdr || { os_code: soRow.code });
        setGeneralObservations(soRow.general_observations || "");
        setOcNumber(ocNum);
        setOcPdfUrl(ocPdf);
        setBillingOcDocuments(attachmentCollections.ocDocuments);
        setPoId(soRow.po_id);
        setRemitoContext({ clientId: poClientId, osCode: soRow.code || "" });
        setRows(merged);
        originalRowsRef.current = new Map(
          merged.map((row) => [invoiceRowKey(row), invoiceRowSnapshot(row)])
        );
        lastSavedPricingSignatureRef.current = pricingAutoSaveSignature(
          merged,
          soRow.general_observations || ""
        );
        setAutoSaveState("saved");
        setAutoSaveMessage("Cambios guardados");
        setError(null);

        // Remitos (escaneados)
        await loadRemitos(effectiveOsId, soRow.po_id);
        await loadExtras(effectiveOsId);
      } catch (err) {
        console.error("[loadRows]", err);
        if (!cancel) setError(err.message || "Error al cargar datos.");
      } finally {
        if (!cancel) setLoading(false);
      }
    }

    loadRows();
    return () => (cancel = true);
  }, [visible, effectiveOsId]);

  // --- patch de filas ---
  const patchRow = (instrument_id, patch) => {
    setRows((prev) =>
      prev.map((r) => {
        if (r.instrument_id !== instrument_id) return r;
        const next = { ...r };
        const qty = patch.qty !== undefined ? num(patch.qty, 1) : num(r.qty, 1);
        const priceDraft = patch.price !== undefined ? patch.price : r.price;
        const price = num(priceDraft, 0);
        next.qty = qty;
        // Mientras se escribe, conservar el texto para no transformar "68."
        // en 68 antes de que el usuario pueda completar "68.50".
        next.price = patch.price !== undefined ? String(priceDraft) : r.price;
        next.subtotal = Number((qty * price).toFixed(2));
        if (patch.note !== undefined) next.note = patch.note ?? "";
        return next;
      })
    );
  };

  // --- Guardar precios + observaciones ---
  const persistPricing = async ({ confirmPriceChanges = false } = {}) => {
    if (!effectiveOsId) return;
    try {
      if (autoSaveTimerRef.current) {
        clearTimeout(autoSaveTimerRef.current);
        autoSaveTimerRef.current = null;
      }

      const changedRows = confirmPriceChanges
        ? formatInvoiceChangeSummary(ordered, originalRowsRef.current)
        : [];
      if (confirmPriceChanges && changedRows.length > 0) {
        const preview = changedRows.slice(0, 5).join("\n");
        const more = changedRows.length > 5 ? `\n...y ${changedRows.length - 5} más.` : "";
        const ok = window.confirm(
          `Detectamos cambios en cantidad o precio de ${changedRows.length} ítem(s):\n\n${preview}${more}\n\n¿Querés guardar igual?`
        );
        if (!ok) return;
      }

      setSaving(true);
      setAutoSaveState("saving");
      setAutoSaveMessage("Guardando cambios...");

      const payload = ordered.map((r) => ({
        os_id: effectiveOsId,
        instrument_id: r.instrument_id,
        qty: num(r.qty, 1),
        price: num(r.price, 0),
        currency: r.currency || "USD BNA",
        note: r.note ?? null,
      }));

      const { error: upsertErr } = await supabase
        .from("service_prices")
        .upsert(payload, { onConflict: "os_id,instrument_id" });
      if (upsertErr) throw upsertErr;

      // Si el instrumento ya forma parte de la factura actual, mantener su nota
      // sincronizada. Así, una observación escrita después de asignarlo también
      // llega a Contabilidad y no queda la copia anterior vacía.
      if (currentInvoiceId && invoiceItems.length > 0) {
        const notesByServicePriceId = new Map(
          ordered
            .filter((row) => row.service_price_id)
            .map((row) => [row.service_price_id, String(row.note ?? "")])
        );
        const changedInvoiceItems = invoiceItems.filter((item) => {
          if (!item?.id || !item?.service_price_id) return false;
          if (!notesByServicePriceId.has(item.service_price_id)) return false;
          return (
            String(item.note ?? "") !== notesByServicePriceId.get(item.service_price_id)
          );
        });

        const updateResults = await Promise.all(
          changedInvoiceItems.map((item) =>
            supabase
              .from("billing_invoice_items")
              .update({
                note: notesByServicePriceId.get(item.service_price_id).trim() || null,
              })
              .eq("id", item.id)
              .eq("invoice_id", currentInvoiceId)
          )
        );
        const noteUpdateError = updateResults.find((result) => result.error)?.error;
        if (noteUpdateError) throw noteUpdateError;

        if (changedInvoiceItems.length > 0) {
          setInvoiceItems((prev) =>
            prev.map((item) =>
              notesByServicePriceId.has(item.service_price_id)
                ? { ...item, note: notesByServicePriceId.get(item.service_price_id) }
                : item
            )
          );
        }
      }

      try {
        await supabase
          .from("service_orders")
          .update({ general_observations: generalObservations || null })
          .eq("id", effectiveOsId);
      } catch {
        // ignore
      }

      const currentIds = ordered.map((r) => r.instrument_id);
      if (currentIds.length > 0) {
        const { data: existing } = await supabase
          .from("service_prices")
          .select("instrument_id")
          .eq("os_id", effectiveOsId);

        const existingIds = new Set((existing || []).map((e) => e.instrument_id));
        const toRemove = [...existingIds].filter((id) => !currentIds.includes(id));

        if (toRemove.length > 0) {
          const { error: deleteErr } = await supabase
            .from("service_prices")
            .delete()
            .eq("os_id", effectiveOsId)
            .in("instrument_id", toRemove);
          if (deleteErr) throw deleteErr;
        }
      }

      setRows((prev) =>
        prev.map((r) => ({
          ...r,
          subtotal: Number((num(r.qty, 1) * num(r.price, 0)).toFixed(2)),
        }))
      );
      originalRowsRef.current = new Map(
        ordered.map((row) => [invoiceRowKey(row), invoiceRowSnapshot(row)])
      );
      lastSavedPricingSignatureRef.current = pricingAutoSaveSignature(
        ordered,
        generalObservations
      );

      setError(null);
      setAutoSaveState("saved");
      setAutoSaveMessage("Cambios guardados");
    } catch (err) {
      console.error("[persistPricing]", err);
      setError(err.message || "Error al guardar.");
      setAutoSaveState("error");
      setAutoSaveMessage("No se pudo autoguardar. Revisá la conexión.");
    } finally {
      setSaving(false);
    }
  };

  useEffect(() => {
    if (!visible || !effectiveOsId || loading) return undefined;

    const signature = pricingAutoSaveSignature(ordered, generalObservations);
    if (!lastSavedPricingSignatureRef.current) {
      lastSavedPricingSignatureRef.current = signature;
      return undefined;
    }
    if (signature === lastSavedPricingSignatureRef.current) return undefined;

    setAutoSaveState("pending");
    setAutoSaveMessage("Cambios pendientes...");

    if (autoSaveTimerRef.current) clearTimeout(autoSaveTimerRef.current);
    autoSaveTimerRef.current = setTimeout(() => {
      persistPricing({ confirmPriceChanges: false });
    }, 800);

    return () => {
      if (autoSaveTimerRef.current) {
        clearTimeout(autoSaveTimerRef.current);
        autoSaveTimerRef.current = null;
      }
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [visible, effectiveOsId, loading, ordered, generalObservations]);

  // --- Facturar ---
  const handleFacturar = async () => {
    if (!effectiveOsId) return;
    try {
      setFacturando(true);

      // Guardar primero precios y observaciones
      await persistPricing({ confirmPriceChanges: false });

      // Si tenemos offerId, marcar automáticamente la oferta como "billing"
      if (offerId) {
        try {
          await supabase.from("offers").update({ status: "billing" }).eq("id", offerId);
          onUpdated?.();
        } catch (e) {
          console.error(
            "[ServicePricingModal] Error al actualizar offers.status a 'billing':",
            e
          );
        }
      }

      if (!currentInvoiceId) {
        alert("Primero seleccioná/creá una factura.");
        return;
      }
      if (!invoiceItems?.length) {
        alert("Esta factura no tiene instrumentos asignados.");
        return;
      }
      
      const next = encodeURIComponent(
        `/contabilidad/facturacion?invoice_id=${currentInvoiceId}`
      );
      const url = `${window.location.origin}/login?next=${next}`;
      
      
      

      const company = "Calins Laboratorio";
      const manager = "Daniel Kemeñy";

      const subjParts = [
        "🧾 Solicitud de facturación",
        header?.os_code ? `— Orden de Servicio ${header.os_code}` : "",
        header?.client_name ? `— ${header.client_name}` : "",
      ].filter(Boolean);

      const subject = encodeURIComponent(subjParts.join(" "));

      const remitosLines =
        remitosConArchivo.length > 0
          ? [
              `📎 Remitos escaneados:`,
              ...remitosConArchivo.map((r) => {
                const label = r.official_number || r.code || "Remito";
                return `- ${label}: ${r.pdf_url}`;
              }),
              ``,
            ]
          : [];

      const ocLines =
        billingOcDocuments.length > 0
          ? [
              `📎 Órdenes de compra:`,
              ...billingOcDocuments.map(
                (document, index) =>
                  `- ${document.label || `OC ${index + 1}`}: ${document.url}`
              ),
              ``,
            ]
          : ocPdfUrl
            ? [`📎 OC${ocNumber ? ` ${ocNumber}` : ""}: ${ocPdfUrl}`, ``]
            : [];

      const extrasLines =
        extrasRows?.length > 0
          ? [
              `➕ Extras / adicionales:`,
              ...extrasRows.map((x) => {
                const qty = num(x.qty, 0);
                const price = num(x.price, 0);
                const sub = qty * price;
                const cur = x.currency || "USD BNA";
                const note = x.note ? ` (${x.note})` : "";
                return `- ${x.description}: ${fmt(qty)} × ${fmt(price)} ${cur} = ${fmt(sub)}${note}`;
              }),
              `Total extras: ${fmt(extrasTotal)}`,
              ``,
            ]
          : [];

      const bodyLines = [
        `Estimado equipo de administración,`,
        ``,
        `Se ha generado una solicitud de facturación para la siguiente orden de servicio:`,
        ``,
        `🔹 *Orden de Servicio:* ${header?.os_code ?? "-"}`,
        `🔹 *Cliente:* ${header?.client_name ?? "-"}`,
        `🔹 *Centro de Costo:* ${ordered[0]?.centro_cto ?? header?.cost_center ?? "-"}`,
        ocNumber ? `🔹 *OC Nº:* ${ocNumber}` : null,
        ``,
        ...ocLines,
        ...remitosLines,
        ...extrasLines,
        `📎 Pode acceder al detalle completo haciendo clic en el siguiente enlace:`,
        `${url}`,
        ``,
        `──────────────────────────────`,
        `👨‍🔧 ${manager}`,
        `${company}`,
        `──────────────────────────────`,
        ``,
        `Gracias por su atención.`,
      ].filter(Boolean);

      const body = encodeURIComponent(bodyLines.join("\n"));

      const to = encodeURIComponent(BILLING_TO.join(","));
      const cc = BILLING_CC.length ? `&cc=${encodeURIComponent(BILLING_CC.join(","))}` : "";
      window.location.href = `mailto:${to}?subject=${subject}${cc}&body=${body}`;
    } catch (err) {
      console.error("[handleFacturar]", err);
      setError(err.message || "No se pudo abrir el correo.");
    } finally {
      setFacturando(false);
    }
  };

  // ---------------- FACTURACIÓN: FACTURAS POR PO ----------------
  const toInputYMD = (value) => {
    if (!value) return "";
    const s = String(value);
    return s.includes("T") ? s.slice(0, 10) : s.slice(0, 10);
  };

  const todayYMD = () => {
    const now = new Date();
    const local = new Date(now.getTime() - now.getTimezoneOffset() * 60000);
    return local.toISOString().slice(0, 10);
  };

  const loadInvoices = async (pPoId) => {
    if (!pPoId) return;
    try {
      setLoadingInvoices(true);
      setInvoiceError(null);

      const { data, error } = await supabase
        .from("billing_invoices")
        .select("id,po_id,requested_at,invoice_number,issued_at,status,note,created_at")
        .eq("po_id", pPoId)
        .order("created_at", { ascending: true });

      if (error) throw error;

      const list = data || [];
      setInvoices(list);
      if (!currentInvoiceId && list.length) {
        setCurrentInvoiceId(list[0].id);
      } else if (currentInvoiceId && !list.some((x) => x.id === currentInvoiceId)) {
        setCurrentInvoiceId(list[0]?.id ?? null);
      }

      await refreshInvoicedMap(list);
    } catch (e) {
      console.error("[loadInvoices]", e);
      setInvoiceError(e.message || "No se pudieron cargar las facturas.");
      setInvoices([]);
      setCurrentInvoiceId(null);
      setInvoicedMap(new Map());
    } finally {
      setLoadingInvoices(false);
    }
  };

  
  const refreshInvoicedMap = async (invoiceList = null) => {
    try {
      const list = invoiceList ?? invoices;
      if (!list?.length) {
        setInvoicedMap(new Map());
        setInvoicedExtraMap(new Map());
        return;
      }
      const ids = list.map((x) => x.id);

      // Trae instrumentos y extras asignados a facturas
      const { data, error } = await supabase
        .from("billing_invoice_items")
        .select("invoice_id,service_price_id,extra_id,service_prices(instrument_id)")
        .in("invoice_id", ids);

      if (error) throw error;

      const map = new Map(); // instrument_id -> Set(invoice_id)
      const extraMap = new Map(); // extra_id -> Set(invoice_id)

      for (const it of data || []) {
        if (it?.extra_id) {
          if (!extraMap.has(it.extra_id)) extraMap.set(it.extra_id, new Set());
          extraMap.get(it.extra_id).add(it.invoice_id);
        }

        const instrumentId = it?.service_prices?.instrument_id;
        if (!instrumentId) continue;
        if (!map.has(instrumentId)) map.set(instrumentId, new Set());
        map.get(instrumentId).add(it.invoice_id);
      }

      setInvoicedMap(map);
      setInvoicedExtraMap(extraMap);
    } catch (e) {
      console.warn("[refreshInvoicedMap]", e?.message || e);
      setInvoicedMap(new Map());
      setInvoicedExtraMap(new Map());
    }
  };

  const loadInvoiceItems = async (invoiceId) => {
    if (!invoiceId) {
      setInvoiceItems([]);
      return;
    }
    try {
      setInvoiceError(null);

      const { data, error } = await supabase
        .from("billing_invoice_items")
        .select(
          "id,invoice_id,service_price_id,extra_id,qty,price,currency,note,created_at,service_prices(instrument_id),service_price_extras(id,description,qty,price,currency,note,os_id)"
        )
        .eq("invoice_id", invoiceId)
        .order("created_at", { ascending: true });

      if (error) throw error;

      const bySp = new Map((ordered || []).map((r) => [r.service_price_id, r]));
      const byExtra = new Map((extrasRows || []).map((x) => [x.id, x]));
      const defaultCentro = ordered?.[0]?.centro_cto || "";

      const enriched = (data || []).map((it) => {
        // EXTRA
        if (it.extra_id) {
          const ex = it.service_price_extras || byExtra.get(it.extra_id);
          const qty = num(it.qty ?? ex?.qty ?? 1, 1);
          const price = num(it.price ?? ex?.price ?? 0, 0);
          return {
            ...it,
            kind: "extra",
            instrument_id: null,
            centro_cto: defaultCentro,
            serv_nro: "EXTRA",
            descripcion: ex?.description ? `EXTRA — ${ex.description}` : "EXTRA",
            row_qty: qty,
            row_price: price,
          };
        }

        // INSTRUMENTO
        const r = bySp.get(it.service_price_id);
        return {
          ...it,
          kind: "service_price",
          instrument_id: r?.instrument_id ?? it?.service_prices?.instrument_id ?? null,
          centro_cto: r?.centro_cto ?? "",
          serv_nro: r?.serv_nro ?? "",
          descripcion: r?.descripcion ?? "",
          row_qty: r?.qty ?? null,
          row_price: r?.price ?? null,
        };
      });

      setInvoiceItems(enriched);
    } catch (e) {
      console.error("[loadInvoiceItems]", e);
      setInvoiceError(e.message || "No se pudieron cargar los ítems de la factura.");
      setInvoiceItems([]);
    }
  };
const currentInvoice = useMemo(
    () => invoices.find((x) => x.id === currentInvoiceId) || null,
    [invoices, currentInvoiceId]
  );

  
  const invoiceTotal = useMemo(() => {
    const bySp = new Map((ordered || []).map((r) => [r.service_price_id, r]));
    const byExtra = new Map((extrasRows || []).map((x) => [x.id, x]));

    return (invoiceItems || []).reduce((acc, it) => {
      // Extra
      if (it.extra_id) {
        const ex = it.service_price_extras || byExtra.get(it.extra_id);
        const qty = num(it.qty ?? ex?.qty ?? 1, 1);
        const price = num(it.price ?? ex?.price ?? 0, 0);
        return acc + qty * price;
      }

      // Instrumento
      const r = bySp.get(it.service_price_id);
      const qty = num(it.qty ?? r?.qty ?? 1, 1);
      const price = num(it.price ?? r?.price ?? 0, 0);
      return acc + qty * price;
    }, 0);
  }, [invoiceItems, ordered, extrasRows]);


  // Cargar facturas cuando tengamos PO
  useEffect(() => {
    if (!visible) return;
    if (!poId) return;
    loadInvoices(poId);
    // eslint-disable-next-line
  }, [visible, poId]);

  // Cargar items al cambiar factura activa
  useEffect(() => {
    if (!visible) return;
    loadInvoiceItems(currentInvoiceId);
    // eslint-disable-next-line
  }, [visible, currentInvoiceId]);

  useEffect(() => {
    if (!visible || !poId) return undefined;

    const channel = supabase
      .channel(`billing-modal-realtime-${poId}`)
      .on(
        "postgres_changes",
        { event: "*", schema: "public", table: "billing_invoices" },
        () => {
          loadInvoices(poId);
        }
      )
      .on(
        "postgres_changes",
        { event: "*", schema: "public", table: "billing_invoice_items" },
        () => {
          loadInvoices(poId);
          if (currentInvoiceId) loadInvoiceItems(currentInvoiceId);
        }
      )
      .subscribe();

    return () => {
      supabase.removeChannel(channel);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [visible, poId, currentInvoiceId]);

  // Mantener formulario sincronizado con factura activa
  useEffect(() => {
    if (!currentInvoice) {
      setInvoiceForm({
        requested_at: "",
        invoice_number: "",
        issued_at: "",
        status: "draft",
        note: "",
      });
      setInvoiceNumberEditable(false);
      setInvoiceStatusEditable(false);
      setRequestedAtEditable(false);
      setIssuedAtEditable(false);
      return;
    }
    setInvoiceForm({
      requested_at: toInputYMD(currentInvoice.requested_at),
      invoice_number: currentInvoice.invoice_number || "",
      issued_at: toInputYMD(currentInvoice.issued_at),
      status: normalizeBillingStatus(currentInvoice.status),
      note: currentInvoice.note || "",
    });
    setInvoiceNumberEditable(false);
    setInvoiceStatusEditable(false);
    setRequestedAtEditable(false);
    setIssuedAtEditable(false);
  }, [currentInvoiceId, currentInvoice]);

  useEffect(() => {
    setRefactureAllowedInstrumentIds(new Set());
    setRefactureAllowedExtraIds(new Set());
  }, [currentInvoiceId]);

  const createInvoice = async () => {
    if (!poId) return;
    try {
      setSavingInvoice(true);
      setInvoiceError(null);

      const { data, error } = await supabase
        .from("billing_invoices")
        .insert({
          po_id: poId,
          status: "draft",
        })
        .select("id,po_id,requested_at,invoice_number,issued_at,status,note,created_at")
        .single();

      if (error) throw error;

      const next = [...(invoices || []), data].sort((a, b) =>
        String(a.created_at).localeCompare(String(b.created_at))
      );
      setInvoices(next);
      setCurrentInvoiceId(data.id);
      setInvoiceNumberEditable(true); // factura nueva: habilitar número directamente
      await refreshInvoicedMap(next);
    } catch (e) {
      console.error("[createInvoice]", e);
      setInvoiceError(e.message || "No se pudo crear la factura.");
    } finally {
      setSavingInvoice(false);
    }
  };

  const saveInvoiceHeader = async () => {
    if (!currentInvoiceId) return;
    try {
      setSavingInvoice(true);
      setInvoiceError(null);

      const requestedAt = invoiceForm.requested_at || "";
      const issuedAt = invoiceForm.issued_at ||
        (normalizeBillingStatus(invoiceForm.status) === "invoiced"
          ? (requestedAt || todayYMD())
          : null);

      if (requestedAt && issuedAt && issuedAt < requestedAt) {
        setInvoiceError("La fecha de emisión no puede ser anterior a la fecha de pedido de facturación.");
        return;
      }

      const payload = {
        requested_at: requestedAt
          ? `${invoiceForm.requested_at}T12:00:00.000Z`
          : null,
        invoice_number: invoiceForm.invoice_number?.trim() || null,
        issued_at: issuedAt,
        status: normalizeBillingStatus(invoiceForm.status),
        note: invoiceForm.note?.trim() || null,
      };

      const { data, error } = await supabase
        .from("billing_invoices")
        .update(payload)
        .eq("id", currentInvoiceId)
        .select("id,po_id,requested_at,invoice_number,issued_at,status,note,created_at")
        .single();

      if (error) throw error;

      setInvoices((prev) => prev.map((x) => (x.id === currentInvoiceId ? data : x)));
      setInvoiceNumberEditable(false);
      setInvoiceStatusEditable(false);
    } catch (e) {
      console.error("[saveInvoiceHeader]", e);
      setInvoiceError(e.message || "No se pudo guardar la factura.");
    } finally {
      setSavingInvoice(false);
    }
  };

  const enableInvoiceNumberEdit = () => {
    if (!currentInvoiceId) return;
    const currentValue = String(currentInvoice?.invoice_number || "").trim() || "sin número";
    const ok = window.confirm(
      `Vas a habilitar la edición del número de factura actual (${currentValue}). Si lo cambiás por error puede impactar la sincronización con contabilidad. ¿Querés continuar?`
    );
    if (!ok) return;
    setInvoiceNumberEditable(true);
  };

  const enableInvoiceStatusEdit = () => {
    if (!currentInvoiceId) return;
    const currentStatus = normalizeBillingStatus(currentInvoice?.status || invoiceForm.status);
    const ok = window.confirm(
      `Vas a habilitar la edición del estado actual (${currentStatus}). Si lo cambiás por error puede afectar la facturación. ¿Querés continuar?`
    );
    if (!ok) return;
    setInvoiceStatusEditable(true);
  };

  const enableRequestedAtEdit = () => {
    if (!currentInvoiceId) return;
    const currentValue = invoiceForm.requested_at || "sin fecha";
    const ok = window.confirm(
      `Vas a habilitar la edición de la fecha de pedido actual (${currentValue}). Si la cambiás por error puede desordenar la trazabilidad de la factura. ¿Querés continuar?`
    );
    if (!ok) return;
    setRequestedAtEditable(true);
  };

  const enableIssuedAtEdit = () => {
    if (!currentInvoiceId) return;
    const currentValue = invoiceForm.issued_at || "sin fecha";
    const ok = window.confirm(
      `Vas a habilitar la edición de la fecha de emisión actual (${currentValue}). Si la cambiás por error puede desordenar la facturación. ¿Querés continuar?`
    );
    if (!ok) return;
    setIssuedAtEditable(true);
  };

  const isLockedInvoiceStatus = (status) => {
    const st = normalizeBillingStatus(status);
    return st === "invoiced" || st === "cancelled";
  };

  const getInstrumentInvoiceIds = (instrumentId) => {
    return invoicedMap.get(instrumentId) || new Set();
  };

  const isInstrumentInCurrentInvoice = (instrumentId) => {
    if (!currentInvoiceId) return false;
    return getInstrumentInvoiceIds(instrumentId).has(currentInvoiceId);
  };

  const isInstrumentInOtherInvoice = (instrumentId) => {
    const invoiceIds = getInstrumentInvoiceIds(instrumentId);
    if (!invoiceIds.size) return false;
    if (!currentInvoiceId) return true;
    for (const invoiceId of invoiceIds) {
      if (invoiceId !== currentInvoiceId) return true;
    }
    return false;
  };

  const isInstrumentRefactureEnabled = (instrumentId) => {
    return refactureAllowedInstrumentIds.has(instrumentId);
  };

  const isExtraRefactureEnabled = (extraId) => {
    return refactureAllowedExtraIds.has(extraId);
  };

  const enableInstrumentRefacture = (row) => {
    if (!currentInvoiceId || !row?.instrument_id) return;
    if (!isInstrumentInOtherInvoice(row.instrument_id)) return;
    if (isInstrumentInCurrentInvoice(row.instrument_id)) return;

    const currentValue = row.descripcion || row.serv_nro || row.instrument_id;
    const ok = window.confirm(
      `El instrumento "${currentValue}" ya fue facturado en otra factura. ¿Querés habilitar su refacturación para poder agregarlo a esta factura?`
    );
    if (!ok) return;

    setRefactureAllowedInstrumentIds((prev) => {
      const next = new Set(prev);
      next.add(row.instrument_id);
      return next;
    });
    setToast({
      type: "warning",
      message: "Refacturación habilitada para este instrumento.",
    });
  };

  const enableExtraRefacture = (extra) => {
    if (!currentInvoiceId || !extra?.id) return;
    const already = invoicedExtraMap.get(extra.id);
    const isInOther = already && (!already.has(currentInvoiceId) || already.size > 1);
    const isInThis = already && already.has(currentInvoiceId);
    if (!isInOther || isInThis) return;

    const currentValue = extra.description || extra.id;
    const ok = window.confirm(
      `El extra "${currentValue}" ya fue facturado en otra factura. ¿Querés habilitar su refacturación para poder agregarlo a esta factura?`
    );
    if (!ok) return;

    setRefactureAllowedExtraIds((prev) => {
      const next = new Set(prev);
      next.add(extra.id);
      return next;
    });
    setToast({
      type: "warning",
      message: "Refacturación habilitada para este extra.",
    });
  };

  const deleteInvoice = async (invoiceId) => {
    if (!invoiceId) return;
    const ok = window.confirm(
      "¿Seguro que querés borrar esta factura? Se eliminarán sus ítems."
    );
    if (!ok) return;

    try {
      setSavingInvoice(true);
      setInvoiceError(null);

      const { error } = await supabase.from("billing_invoices").delete().eq("id", invoiceId);
      if (error) throw error;

      const next = (invoices || []).filter((x) => x.id !== invoiceId);
      setInvoices(next);
      setCurrentInvoiceId(next[0]?.id ?? null);
      setInvoiceItems([]);
      await refreshInvoicedMap(next);
    } catch (e) {
      console.error("[deleteInvoice]", e);
      setInvoiceError(e.message || "No se pudo borrar la factura.");
    } finally {
      setSavingInvoice(false);
    }
  };

  const ensureServicePriceId = async (instrumentId) => {
    // Si ya lo tenemos en rows, listo
    const local = (ordered || []).find((r) => r.instrument_id === instrumentId);
    if (local?.service_price_id) return local.service_price_id;

    // Buscar en BD
    const { data } = await supabase
      .from("service_prices")
      .select("id")
      .eq("os_id", effectiveOsId)
      .eq("instrument_id", instrumentId)
      .maybeSingle();

    if (data?.id) {
      setRows((prev) =>
        prev.map((r) =>
          r.instrument_id === instrumentId ? { ...r, service_price_id: data.id } : r
        )
      );
      return data.id;
    }

    // Si no existe, forzamos autoguardado y volvemos a buscar
    await persistPricing({ confirmPriceChanges: false });
    const { data: data2 } = await supabase
      .from("service_prices")
      .select("id")
      .eq("os_id", effectiveOsId)
      .eq("instrument_id", instrumentId)
      .maybeSingle();

    if (data2?.id) {
      setRows((prev) =>
        prev.map((r) =>
          r.instrument_id === instrumentId ? { ...r, service_price_id: data2.id } : r
        )
      );
      return data2.id;
    }

    return null;
  };

  const addToCurrentInvoice = async (row) => {
    if (!currentInvoiceId) {
      alert("Primero creá o seleccioná una factura.");
      return;
    }
    try {
      setSavingInvoice(true);
      setInvoiceError(null);

      const spId = row?.service_price_id || (await ensureServicePriceId(row.instrument_id));
      if (!spId) throw new Error("No se pudo resolver el precio del instrumento.");

      const already = getInstrumentInvoiceIds(row.instrument_id);
      const isInThis = already.has(currentInvoiceId);
      const isInOther = isInstrumentInOtherInvoice(row.instrument_id);
      const isRefactureEnabled = isInstrumentRefactureEnabled(row.instrument_id);

      if (isInThis) {
        setToast({ type: "info", message: "Este instrumento ya está en esta factura." });
        return;
      }

      if (isInOther && !isRefactureEnabled) {
        setToast({
          type: "info",
          message:
            "Este instrumento ya fue facturado en otra factura. Usá 'Rehabilitar' para volver a agregarlo.",
        });
        return;
      }

      const { error } = await supabase.from("billing_invoice_items").insert({
        invoice_id: currentInvoiceId,
        service_price_id: spId,
        qty: num(row.qty ?? 1, 1),
        price: num(row.price ?? 0, 0),
        currency: row.currency || "USD BNA",
        note: row.note ?? null,
      });

      if (error) throw error;

      await loadInvoiceItems(currentInvoiceId);
      await refreshInvoicedMap();
    } catch (e) {
      console.error("[addToCurrentInvoice]", e);
      setInvoiceError(e.message || "No se pudo agregar el ítem.");
    } finally {
      setSavingInvoice(false);
    }
  };

  
  const addExtraToCurrentInvoice = async (extra) => {
    if (!currentInvoiceId) {
      alert("Primero creá o seleccioná una factura.");
      return;
    }
    if (!extra?.id) return;

    try {
      setSavingInvoice(true);
      setInvoiceError(null);

      const already = invoicedExtraMap.get(extra.id);
      const isInOther = already && (!already.has(currentInvoiceId) || already.size > 1);
      const isInThis = already && already.has(currentInvoiceId);
      const isRefactureEnabled = isExtraRefactureEnabled(extra.id);

      if (isInThis) {
        setToast({ type: "info", message: "Este extra ya está en esta factura." });
        return;
      }

      if (isInOther && !isRefactureEnabled) {
        setToast({
          type: "info",
          message: "Este extra ya fue facturado en otra factura. Usá 'Rehabilitar' para volver a agregarlo.",
        });
        return;
      }

      const { error } = await supabase.from("billing_invoice_items").insert({
        invoice_id: currentInvoiceId,
        extra_id: extra.id,
        qty: num(extra.qty ?? 1, 1),
        price: num(extra.price ?? 0, 0),
        currency: extra.currency || "USD BNA",
        note: extra.note ?? null,
      });

      if (error) throw error;

      await loadInvoiceItems(currentInvoiceId);
      await refreshInvoicedMap();
    } catch (e) {
      console.error("[addExtraToCurrentInvoice]", e);
      setInvoiceError(
        e.message ||
          "No se pudo agregar el extra. Verificá que billing_invoice_items tenga la columna extra_id."
      );
    } finally {
      setSavingInvoice(false);
    }
  };

const removeFromCurrentInvoice = async (item) => {
    if (!currentInvoiceId || !item?.service_price_id) return;
    try {
      setSavingInvoice(true);
      setInvoiceError(null);

      const { error } = await supabase
        .from("billing_invoice_items")
        .delete()
        .eq("invoice_id", currentInvoiceId)
        .eq("service_price_id", item.service_price_id);

      if (error) throw error;

      await loadInvoiceItems(currentInvoiceId);
      await refreshInvoicedMap();
    } catch (e) {
      console.error("[removeFromCurrentInvoice]", e);
      setInvoiceError(e.message || "No se pudo quitar el ítem.");
    } finally {
      setSavingInvoice(false);
    }
  };

  const addRemainingToCurrentInvoice = async () => {
    const pending = (ordered || []).filter(
      (r) => !isInstrumentInCurrentInvoice(r.instrument_id) && !isInstrumentInOtherInvoice(r.instrument_id)
    );
    if (!pending.length) {
      setToast({ type: "info", message: "No hay instrumentos pendientes de facturar." });
      return;
    }
    for (const r of pending) {
      // eslint-disable-next-line no-await-in-loop
      await addToCurrentInvoice(r);
    }
  };

  const addAllToCurrentInvoice = async () => {
    if (!ordered?.length) return;
    const eligible = (ordered || []).filter((r) => {
      if (isInstrumentInCurrentInvoice(r.instrument_id)) return false;
      if (!isInstrumentInOtherInvoice(r.instrument_id)) return true;
      return isInstrumentRefactureEnabled(r.instrument_id);
    });

    if (!eligible.length) {
      setToast({
        type: "info",
        message: "No hay instrumentos habilitados para agregar. Rehabilitá uno primero si querés refacturarlo.",
      });
      return;
    }

    const ok = window.confirm(
      "Esto agregará a la factura actual todos los instrumentos habilitados. Los ya facturados en otra factura quedan bloqueados salvo que los re-habilites manualmente."
    );
    if (!ok) return;
    for (const r of eligible) {
      // eslint-disable-next-line no-await-in-loop
      await addToCurrentInvoice(r);
    }
  };

  // --- Render ---
  if (!visible) return null;

  if (!effectiveOsId && osNotFound) {
    // Oferta sin OS — mostrar solo sección de facturación
    return (
      <div className="fixed inset-0 z-[100] flex items-center justify-center bg-black/60 p-4">
        <div className="w-full max-w-3xl max-h-[90vh] overflow-y-auto rounded-xl bg-neutral-900 text-white p-6 shadow-2xl flex flex-col gap-4">
          <div className="flex items-start justify-between">
            <div>
              <h2 className="text-base font-semibold">Facturación</h2>
              <p className="text-xs text-amber-300 mt-1">
                Esta oferta no tiene una Orden de Servicio vinculada en el sistema. Podés registrar el número de factura directamente.
              </p>
            </div>
            <button onClick={onClose} className="text-white/50 hover:text-white text-xl leading-none">✕</button>
          </div>

          {!poId ? (
            <div className="text-sm text-white/60 rounded-lg border border-white/10 bg-white/5 p-4">
              No se encontró una Orden de Compra interna vinculada a esta oferta. Contactá al administrador para vincularla.
            </div>
          ) : (
            <div className="rounded-lg border border-white/10 bg-white/5 p-4">
              <div className="flex items-center justify-between gap-2 mb-3">
                <div>
                  <div className="text-sm font-semibold">Facturas por OC</div>
                  <div className="text-xs text-white/60">Creá una factura y registrá el número.</div>
                </div>
                <button
                  onClick={createInvoice}
                  disabled={savingInvoice}
                  className="px-3 py-2 rounded-md bg-indigo-600 hover:bg-indigo-500 text-xs disabled:opacity-60"
                >
                  + Nueva factura
                </button>
              </div>

              {invoiceError && <div className="text-xs text-red-300 mb-2">{invoiceError}</div>}

              {invoices.length === 0 ? (
                <div className="text-xs text-white/60">No hay facturas todavía. Creá una con <b>+ Nueva factura</b>.</div>
              ) : (
                <div className="space-y-2">
                  {invoices.map((inv) => (
                    <button
                      key={inv.id}
                      onClick={() => setCurrentInvoiceId(inv.id)}
                      className={`w-full text-left rounded-md border px-3 py-2 text-xs transition ${
                        currentInvoiceId === inv.id
                          ? "border-indigo-500 bg-indigo-500/20"
                          : "border-white/10 bg-white/5 hover:bg-white/10"
                      }`}
                    >
                      <span className="font-medium">{inv.invoice_number || "Sin número"}</span>
                      {inv.issued_at && <span className="ml-2 text-white/50">{inv.issued_at?.slice(0, 10)}</span>}
                    </button>
                  ))}
                </div>
              )}

              {currentInvoiceId && (
                <div className="mt-4 space-y-3 border-t border-white/10 pt-4">
                  <div>
                    <div className="text-xs text-white/70 mb-1">Número de factura</div>
                    <div className="flex gap-2">
                      <input
                        className="flex-1 rounded bg-white/10 border border-white/20 p-2 text-sm disabled:opacity-60 disabled:cursor-not-allowed"
                        value={invoiceForm.invoice_number}
                        placeholder="Ej: A-0001-00012345"
                        disabled={!invoiceNumberEditable}
                        readOnly={!invoiceNumberEditable}
                        onChange={(e) => setInvoiceForm((p) => ({ ...p, invoice_number: e.target.value }))}
                      />
                      {!invoiceNumberEditable && (
                        <button
                          type="button"
                          className="text-[11px] px-2 py-1 rounded bg-white/10 hover:bg-white/20 border border-white/10 whitespace-nowrap"
                          onClick={enableInvoiceNumberEdit}
                        >
                          ✏️ Modificar
                        </button>
                      )}
                    </div>
                  </div>
                  <div className="grid grid-cols-2 gap-3">
                    <div>
                      <div className="text-xs text-white/70 mb-1">Fecha solicitada</div>
                      <input
                        type="date"
                        className="w-full rounded bg-white/10 border border-white/20 p-2 text-sm"
                        value={invoiceForm.requested_at}
                        onChange={(e) => setInvoiceForm((p) => ({ ...p, requested_at: e.target.value }))}
                      />
                    </div>
                    <div>
                      <div className="text-xs text-white/70 mb-1">Fecha emisión</div>
                      <input
                        type="date"
                        className="w-full rounded bg-white/10 border border-white/20 p-2 text-sm"
                        value={invoiceForm.issued_at}
                        onChange={(e) => setInvoiceForm((p) => ({ ...p, issued_at: e.target.value }))}
                      />
                    </div>
                  </div>
                  <div>
                    <div className="text-xs text-white/70 mb-1">Nota</div>
                    <textarea
                      rows={2}
                      className="w-full rounded bg-white/10 border border-white/20 p-2 text-sm resize-none"
                      value={invoiceForm.note}
                      placeholder="Observaciones opcionales"
                      onChange={(e) => setInvoiceForm((p) => ({ ...p, note: e.target.value }))}
                    />
                  </div>
                  <div className="flex justify-end gap-2">
                    <button
                      onClick={saveInvoiceHeader}
                      disabled={savingInvoice}
                      className="px-4 py-2 rounded-md bg-emerald-600 hover:bg-emerald-500 text-xs disabled:opacity-60"
                    >
                      {savingInvoice ? "Guardando…" : "💾 Guardar factura"}
                    </button>
                  </div>
                </div>
              )}
            </div>
          )}

          <div className="text-right">
            <button onClick={onClose} className="px-3 py-2 rounded-md bg-white/10 hover:bg-white/20 text-sm">
              Cerrar
            </button>
          </div>
        </div>
      </div>
    );
  }

  if (!effectiveOsId) {
    return (
      <div className="fixed inset-0 z-[100] flex items-center justify-center bg-black/60 p-4">
        <div className="w-full max-w-lg rounded-xl bg-neutral-900 text-white p-6 shadow-2xl">
          {error ? (
            <div className="text-sm text-red-300">{error}</div>
          ) : (
            <div className="text-sm text-white/80">Resolviendo orden de servicio…</div>
          )}
          <div className="mt-4 text-right">
            <button
              onClick={onClose}
              className="px-3 py-2 rounded-md bg-white/10 hover:bg-white/20 text-sm"
            >
              Cerrar
            </button>
          </div>
        </div>
      </div>
    );
  }

  return (
    <>
      {/* input oculto para subir remito */}
      <input
        ref={fileInputRef}
        type="file"
        accept="application/pdf,image/*"
        className="hidden"
        onChange={handleFileSelected}
      />


      {/* Modal extras */}
      <ExtrasModal
        open={extrasOpen}
        osId={effectiveOsId}
        osCode={header?.os_code}
        onClose={() => setExtrasOpen(false)}
        onChanged={() => loadExtras(effectiveOsId)}
        setToast={setToast}
      />

      <RemitoManagerModal
        open={remitoManagerOpen}
        onClose={() => setRemitoManagerOpen(false)}
        remitos={remitos}
        osId={effectiveOsId}
        poId={poId}
        clientId={remitoContext.clientId}
        osCode={remitoContext.osCode || header?.os_code}
        onSaved={() => loadRemitos(effectiveOsId, poId)}
      />

      {/* Modal principal */}
      <div className="fixed inset-0 z-[100] flex items-start justify-center bg-black/60 p-4">
        <div className="flex max-h-[92vh] w-full max-w-6xl flex-col overflow-hidden rounded-xl bg-neutral-900 text-white shadow-2xl">
          {/* Header */}
          <div className="px-5 py-4 border-b border-white/10 flex items-center justify-between">
            <div>
              <div className="text-lg font-semibold">Ver servicio · Precios</div>
              {header?.os_code && (
                <div className="text-xs text-slate-300">
                  {header.os_code} · {header.client_name || ""}
                </div>
              )}
              {ocNumber ? (
                <div className="text-xs text-emerald-300 mt-0.5">OC Nº: {ocNumber}</div>
              ) : null}
              {ordered[0]?.centro_cto ? (
                <div className="text-xs text-sky-300 mt-0.5">
                  Centro CTO: {ordered[0].centro_cto}
                </div>
              ) : null}
            </div>

            <div className="text-right text-xs text-slate-200 space-y-0.5">
              <div>
                <span className="text-slate-400">Forma de pago:</span>{" "}
                {header?.payment_method || header?.payment_terms || "—"}
              </div>
              <div>
                <span className="text-slate-400">Moneda:</span>{" "}
                {header?.price_currency || header?.currency || "USD BNA"}
              </div>

              {ocPdfUrl && (
                <div className="pt-1">
                  <button
                    onClick={() => setOcViewerOpen(true)}
                    className="px-2 py-1 rounded bg-white/10 hover:bg-white/20 text-xs text-emerald-300 border border-emerald-600/40"
                  >
                    Ver OC
                  </button>
                </div>
              )}

              <div className="pt-1">
                <button
                  onClick={() => setExtrasOpen(true)}
                  className="px-2 py-1 rounded bg-white/10 hover:bg-white/20 text-xs text-sky-300 border border-sky-600/30"
                  title="Agregar/editar ítems extra (in situ, baterías, repuestos, etc.)"
                >
                  Extras
                </button>
              </div>

              {loadingExtras ? (
                <div className="text-[11px] text-white/60 pt-1">Cargando extras…</div>
              ) : extrasError ? (
                <div className="text-[11px] text-red-300 pt-1">{extrasError}</div>
              ) : null}

              <div className="pt-1">
                <div className="text-sm text-white">Total</div>
                <div className="text-xl font-semibold">{fmt(grandTotal)}</div>
                {extrasTotal > 0 ? (
                  <div className="text-xs text-slate-300">Extras: {fmt(extrasTotal)}</div>
                ) : null}
              </div>
            </div>
          </div>

          <div className="border-b border-white/10 bg-neutral-950/40 px-5 py-3">
            <div className="flex flex-col gap-3 lg:flex-row lg:items-center lg:justify-between">
              <div className="inline-flex w-fit overflow-hidden rounded-md border border-white/10 bg-black/20">
                <button
                  type="button"
                  onClick={() => setActivePricingTab("prices")}
                  className={
                    "px-4 py-2 text-sm font-semibold " +
                    (activePricingTab === "prices"
                      ? "bg-sky-600 text-white"
                      : "text-white/70 hover:bg-white/10 hover:text-white")
                  }
                >
                  1. Precios y remitos
                </button>
                <button
                  type="button"
                  onClick={() => setActivePricingTab("billing")}
                  className={
                    "px-4 py-2 text-sm font-semibold " +
                    (activePricingTab === "billing"
                      ? "bg-indigo-600 text-white"
                      : "text-white/70 hover:bg-white/10 hover:text-white")
                  }
                >
                  2. Facturación
                </button>
              </div>

              <div className="grid grid-cols-2 gap-2 text-xs sm:flex sm:items-center sm:justify-end">
                <div className="rounded-md border border-white/10 bg-white/5 px-3 py-2">
                  <div className="text-white/50">Instrumentos</div>
                  <div className="font-semibold text-white">{ordered.length}</div>
                </div>
                <div className="rounded-md border border-white/10 bg-white/5 px-3 py-2">
                  <div className="text-white/50">Facturas</div>
                  <div className="font-semibold text-white">{invoices.length}</div>
                </div>
                <div className="rounded-md border border-white/10 bg-white/5 px-3 py-2">
                  <div className="text-white/50">En factura actual</div>
                  <div className="font-semibold text-white">{invoiceItems.length}</div>
                </div>
                <div className="rounded-md border border-white/10 bg-white/5 px-3 py-2">
                  <div className="text-white/50">Total</div>
                  <div className="font-semibold text-white">{fmt(grandTotal)}</div>
                </div>
              </div>
            </div>
          </div>

          <div className="flex-1 overflow-auto px-5 py-4">
            {activePricingTab === "prices" ? (
              <>
            <div className="mb-3 flex flex-col gap-2 sm:flex-row sm:items-center sm:justify-between">
              <div
                className={
                  "inline-flex w-fit items-center gap-2 rounded-md border px-3 py-1.5 text-xs " +
                  (autoSaveState === "error"
                    ? "border-red-500/40 bg-red-500/10 text-red-200"
                    : autoSaveState === "saving" || autoSaveState === "pending"
                    ? "border-amber-500/40 bg-amber-500/10 text-amber-100"
                    : "border-emerald-500/30 bg-emerald-500/10 text-emerald-200")
                }
              >
                <span
                  className={
                    "h-2 w-2 rounded-full " +
                    (autoSaveState === "error"
                      ? "bg-red-300"
                      : autoSaveState === "saving" || autoSaveState === "pending"
                      ? "bg-amber-300"
                      : "bg-emerald-300")
                  }
                />
                {autoSaveMessage || "Cambios guardados"}
              </div>
              {autoSaveState === "error" ? (
                <button
                  type="button"
                  onClick={() => persistPricing({ confirmPriceChanges: false })}
                  disabled={saving || loading}
                  className="w-fit rounded-md border border-red-400/40 bg-red-500/10 px-3 py-1.5 text-xs text-red-100 hover:bg-red-500/20 disabled:opacity-60"
                >
                  Reintentar guardado
                </button>
              ) : null}
            </div>
            <div className="overflow-x-auto rounded-md border border-white/10">
              <table className="min-w-full text-sm">
                <thead className="bg-white/5">
                  <tr>
                    <th className="px-3 py-2 text-left font-semibold w-24">CENTRO CTO</th>
                    <th className="px-3 py-2 text-left font-semibold w-28">SERV Nro.</th>
                    <th className="px-3 py-2 text-left font-semibold">DESCRIPCIÓN</th>
                    <th className="px-3 py-2 text-right font-semibold w-20">CANT.</th>
                    <th className="px-3 py-2 text-right font-semibold w-28">PU</th>
                    <th className="px-3 py-2 text-right font-semibold w-32">SUBTOTAL</th>
                    <th className="px-3 py-2 text-left font-semibold w-52">Nota</th>
                    <th className="px-3 py-2 text-right font-semibold w-20">Acción</th>
                  </tr>
                </thead>
                <tbody>
                  {ordered.map((r) => (
                    <tr key={r.instrument_id} className="odd:bg-white/0 even:bg-white/5">
                      <td className="px-3 py-2">{r.centro_cto}</td>
                      <td className="px-3 py-2">{r.serv_nro}</td>
                      <td className="px-3 py-2">{r.descripcion}</td>
                      <td className="px-3 py-2 text-right">
                        <input
                          className="w-16 rounded bg-white/10 border border-white/20 p-1 text-right"
                          value={r.qty}
                          onChange={(e) => patchRow(r.instrument_id, { qty: e.target.value })}
                        />
                      </td>
                      <td className="px-3 py-2 text-right">
                        <input
                          type="text"
                          inputMode="decimal"
                          className="w-20 rounded bg-white/10 border border-white/20 p-1 text-right"
                          value={r.price}
                          onChange={(e) => {
                            const value = e.target.value;
                            if (isDecimalDraft(value)) {
                              patchRow(r.instrument_id, { price: value });
                            }
                          }}
                        />
                      </td>
                      <td className="px-3 py-2 text-right">{fmt(r.subtotal)}</td>
                      <td className="px-3 py-2">
                        <input
                          className="w-full rounded bg-white/10 border border-white/20 p-1"
                          value={r.note || ""}
                          placeholder="Observaciones…"
                          onChange={(e) => patchRow(r.instrument_id, { note: e.target.value })}
                        />
                      </td>
                      <td className="px-3 py-2 text-right">
                        <div className="flex items-center justify-end gap-2">
                          <button
                            onClick={() => addToCurrentInvoice(r)}
                            disabled={
                              !currentInvoiceId ||
                              savingInvoice ||
                              isInstrumentInCurrentInvoice(r.instrument_id) ||
                              (isInstrumentInOtherInvoice(r.instrument_id) &&
                                !isInstrumentRefactureEnabled(r.instrument_id))
                            }
                            className={
                              "px-2 py-1 rounded text-xs border " +
                              (isInstrumentInCurrentInvoice(r.instrument_id)
                                ? "bg-emerald-700/40 border-emerald-600/50 text-emerald-200"
                                : isInstrumentInOtherInvoice(r.instrument_id) &&
                                  !isInstrumentRefactureEnabled(r.instrument_id)
                                ? "bg-white/5 border-white/10 text-white/40 cursor-not-allowed"
                                : "bg-white/10 hover:bg-white/20 border-white/20")
                            }
                            title={
                              !currentInvoiceId
                                ? "Seleccioná/creá una factura para asignar instrumentos"
                                : isInstrumentInCurrentInvoice(r.instrument_id)
                                ? "Este instrumento ya está en esta factura"
                                : isInstrumentInOtherInvoice(r.instrument_id) &&
                                  !isInstrumentRefactureEnabled(r.instrument_id)
                                ? "Este instrumento ya fue facturado en otra factura. Rehabilitalo para volver a agregarlo"
                                : "Agregar a la factura actual"
                            }
                          >
                            {isInstrumentInCurrentInvoice(r.instrument_id)
                              ? "✓"
                              : isInstrumentInOtherInvoice(r.instrument_id) &&
                                !isInstrumentRefactureEnabled(r.instrument_id)
                              ? "Bloq."
                              : "+"}
                          </button>
                          {isInstrumentInOtherInvoice(r.instrument_id) &&
                          !isInstrumentRefactureEnabled(r.instrument_id) &&
                          !isInstrumentInCurrentInvoice(r.instrument_id) ? (
                            <button
                              type="button"
                              onClick={() => enableInstrumentRefacture(r)}
                              disabled={!currentInvoiceId || savingInvoice}
                              className="px-2 py-1 rounded text-xs border border-amber-500/40 bg-amber-500/10 hover:bg-amber-500/20 text-amber-200"
                            >
                              Rehabilitar
                            </button>
                          ) : null}
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
                <tfoot>
                  {extrasTotal > 0 ? (
                    <tr className="bg-white/5">
                      <td colSpan={5} className="px-3 py-2 text-right text-white/80">
                        Extras
                      </td>
                      <td className="px-3 py-2 text-right text-white/80">{fmt(extrasTotal)}</td>
                      <td />
                      <td />
                    </tr>
                  ) : null}
                  <tr className="bg-white/5 font-semibold">
                    <td colSpan={5} className="px-3 py-2 text-right">
                      Total
                    </td>
                    <td className="px-3 py-2 text-right">{fmt(grandTotal)}</td>
                    <td />
                    <td />
                  </tr>
                </tfoot>
              </table>
            </div>

            {/* Observaciones generales */}
            <div className="mt-4">
              <div className="mb-1 text-sm text-white/80">Observaciones Generales</div>
              <textarea
                className="w-full min-h-[120px] rounded-lg bg-white/5 border border-white/15 p-3"
                placeholder="Estas observaciones aparecerán en la página de facturación"
                value={generalObservations}
                onChange={(e) => setGeneralObservations(e.target.value)}
              />
            </div>

            {/* REMITOS escaneados */}
            <div className="mt-4 rounded-lg border border-white/10 bg-white/5 p-4">
              <div className="flex flex-col gap-2 sm:flex-row sm:items-center sm:justify-between">
                <div>
                  <div className="text-sm font-semibold">Remitos escaneados</div>
                  <div className="text-[11px] text-white/60">
                    Podés cargar varios remitos para la misma OC/centro de costo.
                  </div>
                </div>
                <div className="flex flex-wrap gap-2">
                  <button
                    type="button"
                    onClick={() => {
                      setRemitoManagerOpen(true);
                      setRemitosError(null);
                    }}
                    className="px-3 py-2 text-xs rounded bg-emerald-700/50 hover:bg-emerald-700/70 border border-emerald-500/30"
                    disabled={loadingRemitos}
                  >
                    Gestionar remitos
                  </button>
                  <button
                    onClick={refreshBillingModal}
                    className="px-2 py-1 text-xs rounded bg-white/10 hover:bg-white/20"
                    disabled={loadingRemitos || loadingInvoices || loadingExtras || !poId}
                  >
                    {loadingRemitos || loadingInvoices || loadingExtras ? "Cargando…" : "Refrescar todo"}
                  </button>
                </div>
              </div>

              {remitosError ? <div className="mt-2 text-xs text-red-300">{remitosError}</div> : null}

              {showNewRemitoForm ? (
                <div className="mt-3 rounded-md border border-emerald-500/20 bg-emerald-500/10 p-3">
                  <div className="grid gap-3 md:grid-cols-4">
                    <div>
                      <label className="mb-1 block text-xs text-white/70">Nº remito</label>
                      <input
                        className="w-full rounded bg-white/10 border border-white/20 p-2 text-sm"
                        value={newRemitoNumber}
                        onChange={(e) => setNewRemitoNumber(e.target.value)}
                        placeholder="Ej: 0001-00005623"
                        disabled={creatingRemito}
                      />
                    </div>
                    <div>
                      <label className="mb-1 block text-xs text-white/70">Fecha salida</label>
                      <input
                        type="date"
                        className="w-full rounded bg-white/10 border border-white/20 p-2 text-sm"
                        value={newRemitoDate}
                        onChange={(e) => setNewRemitoDate(e.target.value)}
                        disabled={creatingRemito}
                      />
                    </div>
                    <div className="md:col-span-2">
                      <label className="mb-1 block text-xs text-white/70">Archivo PDF / imagen</label>
                      <input
                        type="file"
                        accept=".pdf,.png,.jpg,.jpeg,application/pdf,image/png,image/jpeg"
                        className="w-full rounded bg-white/10 border border-white/20 p-1.5 text-sm file:mr-3 file:rounded file:border-0 file:bg-white/10 file:px-3 file:py-1 file:text-white"
                        onChange={(e) => setNewRemitoFile(e.target.files?.[0] || null)}
                        disabled={creatingRemito}
                      />
                    </div>
                    <div className="md:col-span-4">
                      <label className="mb-1 block text-xs text-white/70">Nota</label>
                      <input
                        className="w-full rounded bg-white/10 border border-white/20 p-2 text-sm"
                        value={newRemitoNotes}
                        onChange={(e) => setNewRemitoNotes(e.target.value)}
                        placeholder="Opcional"
                        disabled={creatingRemito}
                      />
                    </div>
                  </div>
                  <div className="mt-3 flex flex-wrap justify-end gap-2">
                    <button
                      type="button"
                      onClick={() => {
                        resetNewRemitoForm();
                        setShowNewRemitoForm(false);
                      }}
                      disabled={creatingRemito}
                      className="rounded-md border border-white/10 bg-white/10 px-3 py-2 text-xs hover:bg-white/20 disabled:opacity-60"
                    >
                      Cancelar
                    </button>
                    <button
                      type="button"
                      onClick={createScannedRemito}
                      disabled={creatingRemito}
                      className="rounded-md bg-emerald-600 px-3 py-2 text-xs hover:bg-emerald-500 disabled:opacity-60"
                    >
                      {creatingRemito ? "Guardando…" : "Guardar remito"}
                    </button>
                  </div>
                </div>
              ) : null}

              {loadingRemitos ? (
                <div className="mt-2 text-xs text-white/70">Cargando remitos…</div>
              ) : remitos.length === 0 ? (
                <div className="mt-2 text-xs text-white/70">No hay remitos asociados a esta OC/centro de costo.</div>
              ) : (
                <div className="mt-3 space-y-2">
                  {remitos.map((r) => {
                    const label = r.official_number || r.code || "Remito";
                    const date = r.date_out ? String(r.date_out) : "";
                    const hasUrl = !!r.pdf_url;
                    const uploadedFileName =
                      r.file_name || fileNameFromStorageUrl(r.pdf_url);

                    return (
                      <div
                        key={r.id}
                        className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-2 rounded-md border border-white/10 bg-black/20 p-3"
                      >
                        <div className="text-xs text-white/80">
                          <div className="font-semibold text-white">{label}</div>
                          <div className="text-white/60">
                            OS: {r.os_code || "—"} · Fecha: {date || "—"} · Estado: {r.status || "—"}
                          </div>
                          {r.notes ? <div className="text-white/60 mt-1">{r.notes}</div> : null}
                          {hasUrl ? (
                            <div
                              className="mt-1 flex min-w-0 items-start gap-1 text-emerald-300"
                              title={uploadedFileName || "Archivo escaneado cargado"}
                            >
                              <span className="shrink-0 font-medium">Archivo:</span>
                              <span className="break-all">
                                {uploadedFileName || "escaneado cargado"}
                              </span>
                            </div>
                          ) : (
                            <div className="text-amber-300 mt-1">Sin escaneo cargado</div>
                          )}
                        </div>

                        <div className="flex gap-2">
                          <button
                            onClick={() => openUploadForRemito(r.id)}
                            disabled={uploadingRemitoId === r.id}
                            className="px-3 py-2 rounded-md bg-orange-600 hover:bg-orange-500 text-xs"
                            title="Subir PDF/imagen escaneada del remito"
                          >
                            {uploadingRemitoId === r.id ? "Subiendo…" : "Subir escaneo"}
                          </button>

                          {hasUrl && (
                            <button
                              onClick={() => window.open(r.pdf_url, "_blank")}
                              className="px-3 py-2 rounded-md bg-white/10 hover:bg-white/20 text-xs"
                            >
                              Ver
                            </button>
                          )}
                        </div>
                      </div>
                    );
                  })}
                </div>
              )}
            </div>
            <div className="mt-4 flex flex-wrap justify-end gap-2">
              <button
                type="button"
                onClick={onClose}
                className="rounded-md border border-white/10 bg-white/10 px-4 py-2 text-sm font-semibold text-white hover:bg-white/20"
              >
                Cerrar
              </button>
              <button
                type="button"
                onClick={() => setActivePricingTab("billing")}
                className="rounded-md bg-indigo-600 px-4 py-2 text-sm font-semibold text-white hover:bg-indigo-500"
              >
                Continuar a facturación
              </button>
            </div>
              </>
            ) : null}

            {/* FACTURAS (multi por OC / PO) */}
            {activePricingTab === "billing" ? (
            <div className="mt-4 rounded-lg border border-white/10 bg-white/5 p-4">
              <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-2">
                <div>
                  <div className="text-sm font-semibold">Facturación · Facturas por OC</div>
                  <div className="text-xs text-white/60">
                    Podés crear varias facturas y asignar instrumentos a cada una (igual que Remitos).
                  </div>
                </div>

                <div className="flex items-center gap-2">
                  <button
                    onClick={createInvoice}
                    className="px-3 py-2 rounded-md bg-indigo-600 hover:bg-indigo-500 text-xs"
                    disabled={savingInvoice || !poId}
                    title="Crear una nueva factura para esta OC"
                  >
                    + Nueva factura
                  </button>
                </div>
              </div>

              {invoiceError ? <div className="mt-2 text-xs text-red-300">{invoiceError}</div> : null}

              <div className="mt-3 grid grid-cols-1 lg:grid-cols-12 gap-4">
                {/* Columna izquierda: listado de facturas */}
                <div className="lg:col-span-4 rounded-md border border-white/10 bg-black/20 p-3">
                  <div className="flex items-center justify-between">
                    <div className="text-xs font-semibold text-white/80">Facturas</div>
                    <div className="text-xs text-white/50">{invoices.length}</div>
                  </div>

                  {invoices.length === 0 ? (
                    <div className="mt-2 text-xs text-white/60">
                      No hay facturas todavía. Creá una con <b>+ Nueva factura</b>.
                    </div>
                  ) : (
                    <div className="mt-3 space-y-2 max-h-[360px] overflow-auto pr-1">
                      {invoices.map((inv, idx) => {
                        const active = inv.id === currentInvoiceId;
                        const label = inv.invoice_number
                          ? `Factura ${inv.invoice_number}`
                          : `Factura #${idx + 1}`;

                        const requested = inv.requested_at ? toInputYMD(inv.requested_at) : "";
                        const issued = inv.issued_at ? toInputYMD(inv.issued_at) : "";

                        return (
                          <button
                            key={inv.id}
                            onClick={() => setCurrentInvoiceId(inv.id)}
                            className={
                              "w-full text-left rounded-md border px-3 py-2 transition " +
                              (active
                                ? "bg-indigo-600/30 border-indigo-500/40"
                                : "bg-white/5 hover:bg-white/10 border-white/10")
                            }
                          >
                            <div className="flex items-center justify-between">
                              <div className="text-sm font-semibold">{label}</div>
                              <div className="text-[11px] text-white/60">{inv.status || "draft"}</div>
                            </div>
                            <div className="text-[11px] text-white/60 mt-1">
                              Pedido: {requested || "—"} · Emitida: {issued || "—"}
                            </div>
                          </button>
                        );
                      })}
                    </div>
                  )}

                  <div className="mt-3 pt-3 border-t border-white/10 flex items-center justify-between">
                    <button
                      onClick={() => deleteInvoice(currentInvoiceId)}
                      disabled={!currentInvoiceId || savingInvoice}
                      className="px-3 py-2 rounded-md bg-red-700/40 hover:bg-red-700/50 text-xs border border-red-600/30"
                    >
                      Eliminar factura
                    </button>

                    <div className="text-right">
                      <div className="text-[11px] text-white/60">Total factura</div>
                      <div className="text-lg font-semibold">{fmt(invoiceTotal)}</div>
                    </div>
                  </div>
                </div>

                {/* Columna derecha: edición factura + items */}
                <div className="lg:col-span-8 rounded-md border border-white/10 bg-black/20 p-3">
                  <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-2">
                    <div>
                      <div className="text-xs font-semibold text-white/80">Detalle de factura</div>
                      <div className="text-[11px] text-white/60">
                        Usá <b>+ Pendientes</b> o <b>+ Todos</b>. Para elegir ítems puntuales, volvé a Precios.
                      </div>
                    </div>

                    <div className="flex flex-wrap gap-2">
                      <button
                        type="button"
                        onClick={() => setActivePricingTab("prices")}
                        className="px-3 py-2 rounded-md bg-white/10 hover:bg-white/20 text-xs border border-white/10"
                      >
                        Ver precios
                      </button>
                      <button
                        onClick={addRemainingToCurrentInvoice}
                        disabled={!currentInvoiceId || savingInvoice}
                        className="px-3 py-2 rounded-md bg-emerald-700/40 hover:bg-emerald-700/50 text-xs border border-emerald-600/30"
                        title="Agregar solo los instrumentos que aún no están en ninguna factura"
                      >
                        + Pendientes
                      </button>
                      <button
                        onClick={addAllToCurrentInvoice}
                        disabled={!currentInvoiceId || savingInvoice}
                        className="px-3 py-2 rounded-md bg-white/10 hover:bg-white/20 text-xs border border-white/10"
                        title="Agregar todos los instrumentos (pedirá confirmación si alguno ya está en otra factura)"
                      >
                        + Todos
                      </button>
                    </div>
                  </div>

                  
                  {/* Extras disponibles (para asignar a una factura) */}
                  <div className="mt-3 rounded-md border border-white/10 bg-black/20 p-3">
                    <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-2">
                      <div>
                        <div className="text-xs font-semibold text-white/80">Extras (ítems varios)</div>
                        <div className="text-[11px] text-white/60">
                          Seleccioná qué extras van a esta factura (igual que instrumentos).
                        </div>
                      </div>
                      <div className="text-[11px] text-white/50">
                        Se actualizan con el botón de refresco general.
                      </div>
                    </div>

                    {(extrasRows || []).length === 0 ? (
                      <div className="mt-2 text-xs text-white/60">No hay extras cargados.</div>
                    ) : (
                      <div className="mt-2 space-y-2">
                        {(extrasRows || []).map((ex) => {
                          const inThis = invoicedExtraMap.get(ex.id)?.has(currentInvoiceId);
                          const inAny = (invoicedExtraMap.get(ex.id)?.size || 0) > 0;
                          const sub = num(ex.qty, 0) * num(ex.price, 0);

                          return (
                            <div
                              key={ex.id}
                              className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-2 rounded-md border border-white/10 bg-neutral-950/30 p-2"
                            >
                              <div className="min-w-0">
                                <div className="text-xs font-semibold break-words">
                                  {ex.description}
                                </div>
                                <div className="text-[11px] text-white/60 break-words">
                                  Cant: {fmt(ex.qty)} · PU: {fmt(ex.price)} · Subtotal:{" "}
                                  <b className="text-white">{fmt(sub)}</b>
                                  {ex.note ? ` · Nota: ${ex.note}` : ""}
                                </div>
                              </div>

                              <div className="flex items-center gap-2 shrink-0">
                                {inAny && !inThis ? (
                                  <button
                                    onClick={() => enableExtraRefacture(ex)}
                                    disabled={!currentInvoiceId || savingInvoice}
                                    className="px-2 py-1 rounded text-xs border bg-amber-500/10 hover:bg-amber-500/20 border-amber-400/30 text-amber-100"
                                    title="Habilitar refacturación de este extra"
                                  >
                                    Rehabilitar
                                  </button>
                                ) : null}
                                <button
                                  onClick={() => addExtraToCurrentInvoice(ex)}
                                  disabled={
                                    !currentInvoiceId ||
                                    savingInvoice ||
                                    (inAny && !inThis && !isExtraRefactureEnabled(ex.id))
                                  }
                                  className={
                                    "px-2 py-1 rounded text-xs border " +
                                    (inThis
                                      ? "bg-emerald-700/40 border-emerald-600/50 text-emerald-200"
                                      : inAny && !isExtraRefactureEnabled(ex.id)
                                      ? "bg-white/5 border-white/10 text-white/40 cursor-not-allowed"
                                      : "bg-white/10 hover:bg-white/20 border-white/20")
                                  }
                                  title={
                                    !currentInvoiceId
                                      ? "Seleccioná/creá una factura"
                                      : inAny && !inThis && !isExtraRefactureEnabled(ex.id)
                                      ? "Este extra ya está en otra factura. Rehabilitalo para usarlo."
                                      : "Agregar a la factura actual"
                                  }
                                >
                                  {inThis ? "✓" : "+ Agregar"}
                                </button>
                              </div>
                            </div>
                          );
                        })}
                      </div>
                    )}
                  </div>

                  {/* Formulario de cabecera */}
                  <div className="mt-3 rounded-md border border-indigo-500/20 bg-indigo-500/10 p-3">
                    <div className="flex flex-col lg:flex-row lg:items-start lg:justify-between gap-3">
                      <div className="min-w-0">
                        <div className="text-sm font-semibold">Contexto de factura</div>
                        <div className="text-[11px] text-white/60">
                          Revisá siempre este resumen antes de guardar cambios.
                        </div>
                        <div className="mt-2 grid grid-cols-1 sm:grid-cols-2 xl:grid-cols-3 gap-x-4 gap-y-1 text-[11px] text-white/80">
                          <div>
                            <span className="text-white/55">OS:</span> {header?.os_code || "—"}
                          </div>
                          <div>
                            <span className="text-white/55">Cliente:</span> {header?.client_name || "—"}
                          </div>
                          <div>
                            <span className="text-white/55">OC:</span> {ocNumber || "—"}
                          </div>
                          <div>
                            <span className="text-white/55">Factura:</span>{" "}
                            {invoiceForm.invoice_number || currentInvoice?.invoice_number || "sin número"}
                          </div>
                          <div>
                            <span className="text-white/55">Estado:</span>{" "}
                            {billingStatusLabel(invoiceForm.status)}
                          </div>
                          <div>
                            <span className="text-white/55">Pedido:</span>{" "}
                            {invoiceForm.requested_at || "—"} ·{" "}
                            <span className="text-white/55">Emisión:</span>{" "}
                            {invoiceForm.issued_at || "—"}
                          </div>
                        </div>
                      </div>
                      <div className="text-[11px] text-white/60">
                        La edición manual de fechas, estado y número queda protegida para reducir errores.
                      </div>
                    </div>
                  </div>

                  <div className="mt-3 grid grid-cols-1 md:grid-cols-2 gap-3">
                    <div>
                      <div className="flex items-center justify-between gap-2">
                        <div className="text-xs text-white/70">Fecha pedido de facturación</div>
                        <button
                          type="button"
                          className="text-[11px] px-2 py-1 rounded bg-white/10 hover:bg-white/20 border border-white/10"
                          onClick={enableRequestedAtEdit}
                          disabled={!currentInvoiceId || savingInvoice || requestedAtEditable}
                        >
                          {requestedAtEditable ? "Edición habilitada" : "Modificar fecha"}
                        </button>
                      </div>
                        <input
                          type="date"
                          className="w-full rounded bg-white/10 border border-white/20 p-2 text-sm disabled:opacity-60 disabled:cursor-not-allowed"
                          value={invoiceForm.requested_at}
                          disabled={!requestedAtEditable}
                          readOnly={!requestedAtEditable}
                          max={invoiceForm.issued_at || undefined}
                          onChange={(e) =>
                            setInvoiceForm((p) => ({ ...p, requested_at: e.target.value }))
                          }
                        />
                      {!requestedAtEditable ? (
                        <div className="mt-1 text-[11px] text-amber-300/80">
                          La fecha queda bloqueada hasta confirmar la modificación.
                        </div>
                      ) : null}
                    </div>

                    <div>
                      <div className="text-xs text-white/70">Estado</div>
                      <div className="flex items-center gap-2">
                        <select
                          className="w-full rounded bg-white/10 border border-white/20 p-2 text-sm disabled:opacity-60 disabled:cursor-not-allowed"
                          value={invoiceForm.status}
                          disabled={isLockedInvoiceStatus(invoiceForm.status) && !invoiceStatusEditable}
                          onChange={(e) => {
                            const nextStatus = e.target.value;
                            setInvoiceForm((p) => ({
                              ...p,
                              status: nextStatus,
                              issued_at:
                                nextStatus === "invoiced" && !p.issued_at
                                  ? (p.requested_at || todayYMD())
                                  : p.issued_at,
                            }));
                          }}
                        >
                          <option value="draft">Borrador</option>
                          <option value="sent">Enviada a facturar</option>
                          <option value="invoiced">Facturada</option>
                          <option value="cancelled">Cerrada</option>
                        </select>
                        {isLockedInvoiceStatus(invoiceForm.status) && !invoiceStatusEditable ? (
                          <button
                            type="button"
                            className="px-2 py-1 rounded bg-white/10 hover:bg-white/20 border border-white/10 text-[11px] whitespace-nowrap"
                            onClick={enableInvoiceStatusEdit}
                            disabled={!currentInvoiceId || savingInvoice}
                          >
                            Modificar
                          </button>
                        ) : null}
                      </div>
                    </div>

                    <div>
                      <div className="flex items-center justify-between gap-2">
                        <div className="text-xs text-white/70">Nº factura</div>
                        <button
                          type="button"
                          className="text-[11px] px-2 py-1 rounded bg-white/10 hover:bg-white/20 border border-white/10"
                          onClick={enableInvoiceNumberEdit}
                          disabled={!currentInvoiceId || savingInvoice || invoiceNumberEditable}
                        >
                          {invoiceNumberEditable ? "Edición habilitada" : "Modificar número de factura"}
                        </button>
                      </div>
                      <input
                        className="w-full rounded bg-white/10 border border-white/20 p-2 text-sm disabled:opacity-60 disabled:cursor-not-allowed"
                        value={invoiceForm.invoice_number}
                        placeholder="Ej: A-0001-00012345"
                        disabled={!invoiceNumberEditable}
                        readOnly={!invoiceNumberEditable}
                        onChange={(e) =>
                          setInvoiceForm((p) => ({ ...p, invoice_number: e.target.value }))
                        }
                      />
                      {!invoiceNumberEditable ? (
                        <div className="mt-1 text-[11px] text-amber-300/80">
                          El campo está bloqueado hasta confirmar la modificación.
                        </div>
                      ) : null}
                    </div>

                    <div>
                      <div className="flex items-center justify-between gap-2">
                        <div className="text-xs text-white/70">Fecha emisión</div>
                        <button
                          type="button"
                          className="text-[11px] px-2 py-1 rounded bg-white/10 hover:bg-white/20 border border-white/10"
                          onClick={enableIssuedAtEdit}
                          disabled={!currentInvoiceId || savingInvoice || issuedAtEditable}
                        >
                          {issuedAtEditable ? "Edición habilitada" : "Modificar fecha"}
                        </button>
                      </div>
                      <input
                        type="date"
                        className="w-full rounded bg-white/10 border border-white/20 p-2 text-sm disabled:opacity-60 disabled:cursor-not-allowed"
                        value={invoiceForm.issued_at}
                        disabled={!issuedAtEditable}
                        readOnly={!issuedAtEditable}
                        min={invoiceForm.requested_at || undefined}
                        onChange={(e) =>
                          setInvoiceForm((p) => ({ ...p, issued_at: e.target.value }))
                        }
                      />
                      {!issuedAtEditable ? (
                        <div className="mt-1 text-[11px] text-amber-300/80">
                          La fecha queda bloqueada hasta confirmar la modificación.
                        </div>
                      ) : null}
                    </div>

                    <div className="md:col-span-2">
                      <div className="text-xs text-white/70">Nota</div>
                      <input
                        className="w-full rounded bg-white/10 border border-white/20 p-2 text-sm"
                        value={invoiceForm.note}
                        placeholder="Observación interna…"
                        onChange={(e) => setInvoiceForm((p) => ({ ...p, note: e.target.value }))}
                      />
                    </div>
                  </div>

                  {/* Items */}
                  <div className="mt-4 rounded-md border border-white/10 overflow-x-auto">
                    <table className="min-w-full text-sm">
                      <thead className="bg-white/5">
                        <tr>
                          <th className="px-3 py-2 text-left font-semibold w-24">CENTRO CTO</th>
                          <th className="px-3 py-2 text-left font-semibold w-28">SERV Nro.</th>
                          <th className="px-3 py-2 text-left font-semibold">DESCRIPCIÓN</th>
                          <th className="px-3 py-2 text-right font-semibold w-24">QTY</th>
                          <th className="px-3 py-2 text-right font-semibold w-28">PRECIO</th>
                          <th className="px-3 py-2 text-right font-semibold w-32">SUBTOTAL</th>
                          <th className="px-3 py-2 text-right font-semibold w-24">Acción</th>
                        </tr>
                      </thead>
                      <tbody>
                        {(invoiceItems || []).length === 0 ? (
                          <tr>
                            <td colSpan={7} className="px-3 py-4 text-center text-xs text-white/60">
                              No hay ítems en esta factura.
                            </td>
                          </tr>
                        ) : (
                          invoiceItems.map((it) => {
                            const qty = num(it.qty ?? it.row_qty ?? 1, 1);
                            const price = num(it.price ?? it.row_price ?? 0, 0);
                            const sub = qty * price;

                            return (
                              <tr key={it.id} className="odd:bg-white/0 even:bg-white/5">
                                <td className="px-3 py-2">{it.centro_cto}</td>
                                <td className="px-3 py-2">{it.serv_nro}</td>
                                <td className="px-3 py-2">{it.descripcion}</td>
                                <td className="px-3 py-2 text-right">{fmt(qty)}</td>
                                <td className="px-3 py-2 text-right">{fmt(price)}</td>
                                <td className="px-3 py-2 text-right">{fmt(sub)}</td>
                                <td className="px-3 py-2 text-right">
                                  <button
                                    onClick={() => removeFromCurrentInvoice(it)}
                                    disabled={savingInvoice}
                                    className="px-2 py-1 rounded bg-red-700/30 hover:bg-red-700/40 text-xs border border-red-600/30 text-red-200"
                                  >
                                    Quitar
                                  </button>
                                </td>
                              </tr>
                            );
                          })
                        )}
                      </tbody>
                      <tfoot>
                        <tr className="bg-white/5 font-semibold">
                          <td colSpan={5} className="px-3 py-2 text-right">
                            Total factura
                          </td>
                          <td className="px-3 py-2 text-right">{fmt(invoiceTotal)}</td>
                          <td />
                        </tr>
                      </tfoot>
                    </table>
                  </div>

                  <div className="mt-4 flex flex-col sm:flex-row sm:items-center sm:justify-between gap-2">
                    <div className="text-[11px] text-white/60">
                      Nota: los <b>Extras</b> se asignan a facturas mediante <span className="font-mono">billing_invoice_items.extra_id</span> (además de instrumentos por <span className="font-mono">service_price_id</span>).
                    </div>

                    <div className="flex gap-2">
                      <button
                        onClick={saveInvoiceHeader}
                        disabled={!currentInvoiceId || savingInvoice}
                        className="px-3 py-2 rounded-md bg-indigo-600 hover:bg-indigo-500 text-xs disabled:opacity-60"
                      >
                        {savingInvoice ? "Guardando…" : "Guardar factura"}
                      </button>
                      <button
                        onClick={handleFacturar}
                        disabled={facturando}
                        className="px-3 py-2 rounded-md bg-emerald-600 hover:bg-emerald-500 text-xs"
                      >
                        {facturando ? "Abriendo correo…" : "Facturar (email)"}
                      </button>
                      <button
                        onClick={onClose}
                        className="px-3 py-2 rounded-md bg-white/10 hover:bg-white/20 text-xs border border-white/10"
                      >
                        Cerrar
                      </button>
                    </div>
                  </div>
                </div>
              </div>
            </div>
            ) : null}

            {error ? (
              <div className="mt-4 text-xs text-red-300 rounded-md border border-red-600/30 bg-red-900/10 p-3">
                {error}
              </div>
            ) : null}
          </div>

          {/* OC Viewer */}
          {ocViewerOpen && ocPdfUrl ? (
            <div className="fixed inset-0 z-[110] bg-black/70 p-4 flex items-center justify-center">
              <div className="w-full max-w-5xl bg-neutral-900 rounded-xl border border-white/10 overflow-hidden shadow-2xl">
                <div className="px-4 py-3 border-b border-white/10 flex items-center justify-between text-white">
                  <div className="text-sm font-semibold">OC · Visor</div>
                  <button
                    onClick={() => setOcViewerOpen(false)}
                    className="px-3 py-2 rounded-md bg-white/10 hover:bg-white/20 text-xs"
                  >
                    Cerrar
                  </button>
                </div>
                <div className="h-[75vh] bg-black">
                  <iframe
                    title="OC PDF"
                    src={ocPdfUrl}
                    className="w-full h-full"
                    style={{ border: 0 }}
                  />
                </div>
              </div>
            </div>
          ) : null}
        </div>
      </div>
    </>
  );
}
